import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { db } from "../../lib/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  DndContext,
  closestCorners,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./GoalsProgress.css";

const KANBAN_COLUMNS = ["todo", "in-progress", "done"];
const KANBAN_COLUMN_LABELS = {
  todo: "To Do",
  "in-progress": "In Progress",
  done: "Done",
};

const DEFAULT_KANBAN_CARDS = [
  { id: "task-1", text: "Put away clothes" },
  { id: "task-2", text: "Get a haircut" },
  { id: "task-3", text: "Clean up office" },
  { id: "task-4", text: "Do taxes" },
  { id: "task-5", text: "Change ADT batteries" },
  { id: "task-6", text: "Reschedule physical" },
  { id: "task-7", text: "Put dentist appointment on calendar" },
  { id: "task-8", text: "Schedule gutter cleaning" },
  { id: "task-9", text: "Fix Bluetooth and WiFi on computer" },
];

const DEFAULT_KANBAN = {
  todo: DEFAULT_KANBAN_CARDS.map((c) => c.id),
  "in-progress": [],
  done: [],
  cards: DEFAULT_KANBAN_CARDS,
  nextId: 10,
};

const DEFAULT_HOBBY_KANBAN = {
  todo: [],
  "in-progress": [],
  done: [],
  cards: [],
  nextId: 1,
};

const JOURNAL_AREAS = [
  { id: "fitness", label: "Fitness", emoji: "💪" },
  { id: "writing", label: "Writing", emoji: "✍️" },
  { id: "miniatures", label: "Miniatures / Modeling", emoji: "🖖" },
  { id: "home", label: "Home Improvement", emoji: "🏠" },
  { id: "youtube-russian", label: "YouTube / Russian", emoji: "🎥" },
  { id: "games-apps", label: "Game Design / Apps", emoji: "🎮" },
  { id: "music", label: "Music", emoji: "🎸" },
  { id: "relationships", label: "Relationship Goals", emoji: "❤️" },
];

const DEFAULT_BACK_BURNER_GOAL_IDS = [
  "develop-vietnam-wargame",
  "develop-eastern-front-mobile-game",
];

const MAJOR_OUTCOMES = [
  {
    id: "pay-off-house",
    label: "Pay Off House",
    icon: "🏡",
    targetType: "house-equity-percent",
    targetValue: 100,
    linkedGoalIds: [],
  },
  {
    id: "lose-40-pounds",
    label: "Lose 40 Pounds",
    icon: "⚖️",
    targetType: "weight-loss",
    targetValue: 40,
    linkedGoalIds: ["get-active", "swim-sessions", "golf-sessions"],
  },
  {
    id: "get-published",
    label: "Get Published",
    icon: "📚",
    targetType: "linked-goals-average",
    targetValue: 100,
    linkedGoalIds: [
      "short-story-collection",
      "writing-submissions",
      "write-novel",
    ],
  },
  {
    id: "quit-weed",
    label: "Quit Weed",
    icon: "🌿",
    targetType: "sobriety-days",
    targetValue: 365,
    linkedGoalIds: [],
  },
  {
    id: "get-a-promotion",
    label: "Get a Promotion",
    icon: "📈",
    hidden: true,
    targetType: "linked-goals-average",
    targetValue: 75,
    linkedGoalIds: [
      "grow-youtube",
      "cinemaze-downloads",
      "writing-submissions",
    ],
  },
];

const buildDefaultMajorOutcomeSettings = () => {
  return MAJOR_OUTCOMES.reduce((acc, outcome) => {
    acc[outcome.id] = {
      label: outcome.label,
      icon: outcome.icon,
      targetType: outcome.targetType,
      targetValue: outcome.targetValue,
      linkedGoalIds: [...outcome.linkedGoalIds],
    };
    return acc;
  }, {});
};

const getLocalDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatShortDate = (date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const formatDateKeyLabel = (dateKey) => {
  const [y, m, d] = dateKey.split("-").map(Number);
  return formatShortDate(new Date(y, m - 1, d));
};

const getWeekLabelFromWeekStart = (weekStart) => {
  const [wy, wm, wd] = weekStart.split("-").map(Number);
  const weekStartDate = new Date(wy, wm - 1, wd);
  const weekEndDate = new Date(wy, wm - 1, wd + 6);
  return `${formatShortDate(weekStartDate)} – ${formatShortDate(weekEndDate)}`;
};

const parseDateKeyToDate = (dateKey) => {
  const [y, m, d] = String(dateKey || "")
    .split("-")
    .map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const addDaysToDateKey = (dateKey, daysToAdd) => {
  const start = parseDateKeyToDate(dateKey);
  if (!start) return "";
  const next = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + daysToAdd,
  );
  return getLocalDateKey(next);
};

const getCurrentWeekStartKey = (date = new Date()) => {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.getFullYear(), date.getMonth(), diff);
  return getLocalDateKey(monday);
};

const CONTRACT_PROGRAM_START_KEY = "2026-06-01";

const normalizeContractStartKey = (dateKey) => {
  const parsed = parseDateKeyToDate(dateKey);
  if (!parsed) return CONTRACT_PROGRAM_START_KEY;
  const normalized = getLocalDateKey(parsed);
  return normalized < CONTRACT_PROGRAM_START_KEY
    ? CONTRACT_PROGRAM_START_KEY
    : normalized;
};

const buildQuarterWeekStarts = (quarterStart, totalWeeks = 13) => {
  if (!quarterStart) return [];
  return Array.from({ length: totalWeeks }, (_, i) =>
    addDaysToDateKey(quarterStart, i * 7),
  );
};

const buildDefaultContractConfig = () => ({
  amountOwed: 500,
  quarterStart: CONTRACT_PROGRAM_START_KEY,
  totalWeeks: 13,
  requiredPassingWeeks: 13,
  bikeTarget: 3,
  swimTarget: 1,
  writingSessionsTarget: 2,
  writingWordsTarget: 1500,
  weedQuitDate: addDaysToDateKey(CONTRACT_PROGRAM_START_KEY, 90),
});

const mergeWeeklyAccomplishments = (existing = [], incoming = []) => {
  const merged = [...existing];
  incoming.forEach((text) => {
    if (!merged.includes(text)) merged.push(text);
  });
  return merged;
};

const upsertWeeklyArchiveEntry = (archive = [], entry) => {
  if (!entry || !entry.weekStart) return archive;
  const existingIndex = archive.findIndex(
    (w) => w.weekStart === entry.weekStart,
  );
  if (existingIndex === -1) {
    return [
      {
        ...entry,
        weekLabel:
          entry.weekLabel || getWeekLabelFromWeekStart(entry.weekStart),
        accomplished: entry.accomplished || [],
      },
      ...archive,
    ];
  }

  const existing = archive[existingIndex];
  const mergedEntry = {
    ...existing,
    ...entry,
    weekLabel:
      existing.weekLabel ||
      entry.weekLabel ||
      getWeekLabelFromWeekStart(entry.weekStart),
    accomplished: mergeWeeklyAccomplishments(
      existing.accomplished || [],
      entry.accomplished || [],
    ),
    archivedAt: Math.max(existing.archivedAt || 0, entry.archivedAt || 0),
  };

  return [
    mergedEntry,
    ...archive.filter((_, index) => index !== existingIndex),
  ];
};

const normalizeWeeklyArchive = (archive = []) => {
  return archive.reduce(
    (acc, entry) => upsertWeeklyArchiveEntry(acc, entry),
    [],
  );
};

const GOALS_CONFIG = [
  {
    id: "grow-youtube",
    name: "Grow YouTube",
    type: "count",
    target: "5,000 subscribers",
    targetValue: 5000,
    unit: "subscribers",
    description: "YouTube subscriber growth",
  },
  {
    id: "launch-apps",
    name: "Launch Apps",
    type: "percentage",
    target: "100% complete",
    targetValue: 100,
    description: "App development and launch",
    hidden: true, // Hidden but kept for later
  },
  {
    id: "restart-writing",
    name: "Restart Writing Routine",
    type: "percentage",
    target: "100% complete",
    targetValue: 100,
    description: "Establish consistent writing habit",
    hidden: true, // Hidden but kept for later
  },
  {
    id: "cannabis-sobriety",
    name: "Cannabis Sobriety",
    type: "sobriety",
    target: "Stay sober",
    quitDate: "2026-01-14",
    startTime: "10:30",
    weeklyCost: 150,
    description: "Track time sober from cannabis and money saved",
    hidden: true, // Hidden but kept for later
  },
  {
    id: "short-story-collection",
    name: "Complete Short Story Collection",
    type: "count",
    target: "10 stories",
    targetValue: 10,
    startValue: 6,
    unit: "stories",
    description: "10 polished stories",
    hasIdeasList: true,
    ideasPlaceholder: "Drop a story idea...",
  },
  {
    id: "record-lp",
    name: "Record an LP",
    type: "count",
    target: "12 songs",
    targetValue: 12,
    startValue: 0,
    unit: "songs",
    description: "Write, record, and finish 12 songs for an LP",
    hasIdeasList: true,
    ideasPlaceholder: "Song idea, title, or concept...",
  },
  {
    id: "writing-submissions",
    name: "Submit Writing — 20 Submissions",
    type: "count",
    target: "20 submissions",
    targetValue: 20,
    startValue: 0,
    unit: "submissions",
    description: "Journals, contests & agents",
  },
  {
    id: "get-active",
    name: "Biking — Get on the Road",
    type: "weekly-habit",
    target: "3 rides per week",
    targetValue: 3,
    unit: "rides",
    description: "Bike rides",
  },
  {
    id: "swim-sessions",
    name: "Swim — Once a Week",
    type: "weekly-habit",
    target: "1 session per week",
    targetValue: 1,
    unit: "sessions",
    description: "Swimming sessions",
  },
  {
    id: "writing-weekly-accountability",
    name: "Writing — Weekly Accountability",
    type: "weekly-dual-count",
    target: "2 sessions or 1,500 words per week",
    targetValue: 2,
    unit: "sessions",
    secondaryTargetValue: 1500,
    secondaryUnit: "words",
    description: "2 writing sessions OR 1,500 words each week",
  },
  {
    id: "weed-quit-contract",
    name: "Quit Weed — Contract Streak",
    type: "quit-date-streak",
    target: "Quit by date, then keep streak",
    targetValue: 90,
    unit: "days",
    description:
      "Set quit-by date, click Start when you begin, then track streak days.",
  },
  {
    id: "fishing-sessions",
    name: "Fishing — Once a Week",
    type: "weekly-habit",
    target: "1 session per week",
    targetValue: 1,
    unit: "sessions",
    description: "Fishing trips",
  },
  {
    id: "golf-sessions",
    name: "Golf — Get on the Course",
    type: "count",
    target: "30 sessions this year",
    targetValue: 30,
    startValue: 0,
    unit: "sessions",
    description: "Range sessions & rounds played",
  },
  {
    id: "cinemaze-downloads",
    name: "CineMaze — App Downloads",
    type: "count",
    target: "1,000 downloads",
    targetValue: 1000,
    startValue: 0,
    unit: "downloads",
    description: "Apple App Store downloads",
    actionLink:
      "https://appstoreconnect.apple.com/trends/sales?measure=units_utc&period=day&interval=month&endDate=2026-04-30&startDate=2003-04-01",
    actionLabel: "Open App Store Connect",
  },
  {
    id: "write-novel",
    name: "Write a New Novel",
    type: "count",
    target: "85,000 words",
    targetValue: 85000,
    startValue: 0,
    unit: "words",
    weeklySessionTarget: 3,
    weeklyWordFloor: 1500,
    description:
      "Complete a new novel. Minimum pace: 3 writing sessions/week or 1,500 words/week.",
  },
  {
    id: "develop-vietnam-wargame",
    name: "Develop Vietnam Wargame",
    type: "percentage",
    target: "100% complete",
    targetValue: 100,
    description: "Develop and ship the Vietnam wargame",
  },
  {
    id: "develop-eastern-front-mobile-game",
    name: "Develop Eastern Front Mobile Game",
    type: "percentage",
    target: "100% complete",
    targetValue: 100,
    description: "Develop and ship the Eastern Front mobile game",
  },
];

// Friend's custom goals based on their image
const FRIEND_GOALS_CONFIG = [
  {
    id: "lift",
    name: "lift 3x a week",
    type: "streak",
    target: "52 weeks in a row",
    targetValue: 52,
    weeklyTarget: 3,
    unit: "weeks",
    description: "Consecutive weeks hitting 3x lifting",
  },
  {
    id: "cardio",
    name: "cardio 2x a week",
    type: "streak",
    target: "52 weeks in a row",
    targetValue: 52,
    weeklyTarget: 2,
    unit: "weeks",
    description: "Consecutive weeks hitting 2x cardio",
  },
  {
    id: "no-games",
    name: "no video games 3x a week",
    type: "streak",
    target: "52 weeks in a row",
    targetValue: 52,
    weeklyTarget: 3,
    unit: "weeks",
    description: "Consecutive weeks with 3+ no-game days",
  },
  {
    id: "books-friend",
    name: "finish 10 books before end of year (9/10)",
    type: "count",
    target: "10 books by Dec 31",
    targetValue: 10,
    startValue: 9,
    unit: "books",
    description: "Books completed (currently at 9)",
  },
];

// Sortable Goal Card Component
function SortableGoalCard({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="goal-item">
        <div className="drag-handle" {...attributes} {...listeners}>
          ⋮⋮
        </div>
        {children}
      </div>
    </div>
  );
}

// Sortable Kanban Card Component
function SortableKanbanCard({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="kanban-card"
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

// Sortable Journal Area Tile
function SortableJournalArea({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="journal-area-card">
      <div className="journal-area-drag-handle" {...attributes} {...listeners}>
        ⋮⋮
      </div>
      {children}
    </div>
  );
}

// Droppable Burner Section
function BurnerDropZone({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`burner-drop-zone ${isOver ? "burner-over" : ""}`}
    >
      {children}
    </div>
  );
}

// Droppable Kanban Column Component
function KanbanColumnDroppable({ id, children }) {
  const { setNodeRef } = useDroppable({ id });

  return (
    <div ref={setNodeRef} className="kanban-column-drop-zone">
      {children}
    </div>
  );
}

export default function GoalsProgress() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userGoalsConfig, setUserGoalsConfig] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [goalOrder, setGoalOrder] = useState([]);
  const [frontBurner, setFrontBurner] = useState([]);
  const [backBurner, setBackBurner] = useState([]);
  const [checklistVisibility, setChecklistVisibility] = useState({});
  const [kanban, setKanban] = useState(DEFAULT_KANBAN);
  const [kanbanArchive, setKanbanArchive] = useState([]);
  const [showKanbanArchive, setShowKanbanArchive] = useState(false);
  const [newTaskText, setNewTaskText] = useState("");
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [hobbyKanban, setHobbyKanban] = useState(DEFAULT_HOBBY_KANBAN);
  const [hobbyKanbanArchive, setHobbyKanbanArchive] = useState([]);
  const [showHobbyArchive, setShowHobbyArchive] = useState(false);
  const [newHobbyText, setNewHobbyText] = useState("");
  const [newHobbySubtitle, setNewHobbySubtitle] = useState("");
  const [draggedHobbyId, setDraggedHobbyId] = useState(null);
  const [weeklyGoals, setWeeklyGoals] = useState({
    weekStart: "",
    goals: [],
    nextId: 1,
  });
  const [weeklyGoalsArchive, setWeeklyGoalsArchive] = useState([]);
  const [newWeeklyGoalText, setNewWeeklyGoalText] = useState("");
  const [showWeeklyArchive, setShowWeeklyArchive] = useState(false);
  const [centerPanel, setCenterPanel] = useState("goals");
  const [leftPanel, setLeftPanel] = useState("weekly");
  const [rightPanel, setRightPanel] = useState("money");
  const [contractConfig, setContractConfig] = useState(() =>
    buildDefaultContractConfig(),
  );
  const [contractWeekLogs, setContractWeekLogs] = useState({});
  const [contractFinalizedAt, setContractFinalizedAt] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalDrafts, setJournalDrafts] = useState({});
  const [expandedJournalDays, setExpandedJournalDays] = useState({});
  const [isJournalCollapsed, setIsJournalCollapsed] = useState(false);
  const [editingJournalId, setEditingJournalId] = useState(null);
  const [editingJournalText, setEditingJournalText] = useState("");
  const [journalAreaOrder, setJournalAreaOrder] = useState(() =>
    JOURNAL_AREAS.map((a) => a.id),
  );
  const [noDrinkingQuitDate, setNoDrinkingQuitDate] = useState(
    "2026-04-20T00:00:00",
  );
  const [expandedGoals, setExpandedGoals] = useState({});
  const [expandedKanbanCards, setExpandedKanbanCards] = useState({});
  const [subtaskInputs, setSubtaskInputs] = useState({});
  const [weightLog, setWeightLog] = useState([]);
  const [weightInput, setWeightInput] = useState("");
  const weightCanvasRef = useRef(null);
  const [thcLog, setThcLog] = useState([]);
  const thcCanvasRef = useRef(null);
  const [portfolioValue, setPortfolioValue] = useState("");
  const [checkingValue, setCheckingValue] = useState("");
  const [houseValue, setHouseValue] = useState("");
  const [mortgageDebtValue, setMortgageDebtValue] = useState("");
  const [rav4DebtValue, setRav4DebtValue] = useState("");
  const [mercedesDebtValue, setMercedesDebtValue] = useState("");
  const [creditCardDebtValue, setCreditCardDebtValue] = useState("");
  const [majorOutcomeSettings, setMajorOutcomeSettings] = useState(() =>
    buildDefaultMajorOutcomeSettings(),
  );
  const [editingMajorOutcomes, setEditingMajorOutcomes] = useState({});

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }),
    [],
  );

  const parseFinanceValue = useCallback((value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const netWorthSnapshot = useMemo(() => {
    const portfolio = parseFinanceValue(portfolioValue);
    const checking = parseFinanceValue(checkingValue);
    const house = parseFinanceValue(houseValue);
    const mortgageDebt = Math.abs(parseFinanceValue(mortgageDebtValue));
    const rav4Debt = Math.abs(parseFinanceValue(rav4DebtValue));
    const mercedesDebt = Math.abs(parseFinanceValue(mercedesDebtValue));
    const creditCardDebt = Math.abs(parseFinanceValue(creditCardDebtValue));
    const totalDebt = mortgageDebt + rav4Debt + mercedesDebt + creditCardDebt;
    const netWorth = portfolio + checking + house - totalDebt;

    return {
      portfolio,
      checking,
      house,
      mortgageDebt,
      rav4Debt,
      mercedesDebt,
      creditCardDebt,
      totalDebt,
      netWorth,
    };
  }, [
    portfolioValue,
    checkingValue,
    houseValue,
    mortgageDebtValue,
    rav4DebtValue,
    mercedesDebtValue,
    creditCardDebtValue,
    parseFinanceValue,
  ]);

  const thcDailyTotals = useMemo(() => {
    const totalsByDay = thcLog.reduce((acc, entry) => {
      const fallbackDate = new Date(entry.timestamp || Date.now());
      const key = entry.dateKey || getLocalDateKey(fallbackDate);
      const dose = Number(entry.doseMg) || 0;
      acc[key] = (acc[key] || 0) + dose;
      return acc;
    }, {});

    return Object.entries(totalsByDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateKey, totalMg]) => ({
        dateKey,
        dateLabel: formatDateKeyLabel(dateKey),
        totalMg,
      }));
  }, [thcLog]);

  const todayThcTotalMg = useMemo(() => {
    const todayKey = getLocalDateKey(new Date());
    const today = thcDailyTotals.find((day) => day.dateKey === todayKey);
    return today ? today.totalMg : 0;
  }, [thcDailyTotals]);

  const partitionGoalIds = useCallback((goalIds, preferredBackIds = []) => {
    const preferredBackSet = new Set(preferredBackIds);
    return {
      front: goalIds.filter((id) => !preferredBackSet.has(id)),
      back: goalIds.filter((id) => preferredBackSet.has(id)),
    };
  }, []);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Custom collision detection: prefer droppable columns, then fall back to closest corners
  const kanbanCollisionDetection = useCallback((args) => {
    // First check if pointer is within any droppable column
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      // Prefer column droppables over card sortables
      const columnHit = pointerCollisions.find((c) =>
        KANBAN_COLUMNS.includes(c.id),
      );
      if (columnHit) {
        // Also check if there's a card under the pointer in that column
        const cardHit = pointerCollisions.find(
          (c) => !KANBAN_COLUMNS.includes(c.id),
        );
        return cardHit ? [cardHit] : [columnHit];
      }
      return pointerCollisions;
    }
    // Fall back to rect intersection for edge cases
    const rectCollisions = rectIntersection(args);
    if (rectCollisions.length > 0) return rectCollisions;
    return closestCorners(args);
  }, []);

  // Parse allowed users from environment variable
  const getAllowedUsers = () => {
    const usersString = process.env.REACT_APP_ALLOWED_USERS || "";
    const users = {};
    usersString.split(",").forEach((pair) => {
      const [username, password] = pair.split(":");
      if (username && password) {
        users[password.trim()] = username.trim();
      }
    });
    return users;
  };

  // Check authorization
  useEffect(() => {
    const key = searchParams.get("key");
    const allowedUsers = getAllowedUsers();

    console.log("🔑 Admin Auth Debug:");
    console.log("  URL key:", key);
    console.log("  Allowed users:", Object.keys(allowedUsers).length);

    if (key && allowedUsers[key]) {
      const username = allowedUsers[key];
      console.log("✅ Authorization successful for user:", username);
      setIsAuthorized(true);
      setCurrentUser(username);

      // Set goals config based on user
      let userConfig;
      if (username === "antoin") {
        userConfig = FRIEND_GOALS_CONFIG;
      } else {
        userConfig = GOALS_CONFIG;
      }

      // Filter out hidden goals
      userConfig = userConfig.filter((goal) => !goal.hidden);

      setUserGoalsConfig(userConfig);

      // Set initial goal order based on config
      setGoalOrder(userConfig.map((g) => g.id));

      // Set initial active category based on first category in config
      const firstCategory = userConfig.find((g) => g.category)?.category;
      setActiveCategory(firstCategory || null);
    } else {
      console.log("❌ Authorization failed - redirecting to home");
      navigate("/");
    }
  }, [searchParams, navigate]);

  // Load progress from Firestore
  useEffect(() => {
    if (!isAuthorized || !currentUser) return;

    const loadProgress = async () => {
      try {
        // Use currentUser as the document ID
        const docRef = doc(db, "goals", currentUser);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setProgress(data);

          // Load kanban data if it exists
          if (data.kanban && data.kanban.cards) {
            setKanban(data.kanban);
          }
          if (Array.isArray(data.kanbanArchive)) {
            setKanbanArchive(data.kanbanArchive);
          }

          // Load hobby kanban data if it exists
          if (data.hobbyKanban && data.hobbyKanban.cards) {
            setHobbyKanban(data.hobbyKanban);
          }
          if (Array.isArray(data.hobbyKanbanArchive)) {
            setHobbyKanbanArchive(data.hobbyKanbanArchive);
          }

          // Load weekly goals — auto-archive if week has rolled over
          {
            const currentWeekStart = getCurrentWeekStart();
            const savedWeeklyGoals = data.weeklyGoals;
            const savedArchive = normalizeWeeklyArchive(
              Array.isArray(data.weeklyGoalsArchive)
                ? data.weeklyGoalsArchive
                : [],
            );

            if (
              savedWeeklyGoals &&
              typeof savedWeeklyGoals === "object" &&
              savedWeeklyGoals.weekStart &&
              savedWeeklyGoals.weekStart !== currentWeekStart
            ) {
              // Week rolled over — archive done goals from prior week
              const doneGoals = (savedWeeklyGoals.goals || []).filter(
                (g) => g.done,
              );
              if (doneGoals.length > 0) {
                const archiveEntry = {
                  weekStart: savedWeeklyGoals.weekStart,
                  weekLabel: getWeekLabelFromWeekStart(
                    savedWeeklyGoals.weekStart,
                  ),
                  accomplished: doneGoals.map((g) => g.text),
                  archivedAt: Date.now(),
                };
                setWeeklyGoalsArchive(
                  upsertWeeklyArchiveEntry(savedArchive, archiveEntry),
                );
              } else {
                setWeeklyGoalsArchive(savedArchive);
              }
              setWeeklyGoals({
                weekStart: currentWeekStart,
                goals: [],
                nextId: savedWeeklyGoals.nextId || 1,
              });
            } else if (
              savedWeeklyGoals &&
              typeof savedWeeklyGoals === "object"
            ) {
              setWeeklyGoals({
                weekStart: savedWeeklyGoals.weekStart || currentWeekStart,
                goals: savedWeeklyGoals.goals || [],
                nextId: savedWeeklyGoals.nextId || 1,
              });
              setWeeklyGoalsArchive(savedArchive);
            } else {
              setWeeklyGoals({
                weekStart: currentWeekStart,
                goals: [],
                nextId: 1,
              });
              setWeeklyGoalsArchive(savedArchive);
            }
          }

          // Load weight log if it exists
          if (data.weightLog && Array.isArray(data.weightLog)) {
            setWeightLog(data.weightLog);
          }

          // Load THC log if it exists
          if (data.thcLog && Array.isArray(data.thcLog)) {
            setThcLog(data.thcLog);
          }

          // Load journal entries if they exist
          if (data.journalEntries && Array.isArray(data.journalEntries)) {
            setJournalEntries(data.journalEntries);
          }

          if (typeof data.isJournalCollapsed === "boolean") {
            setIsJournalCollapsed(data.isJournalCollapsed);
          }

          // Load journal area order
          if (data.journalAreaOrder && Array.isArray(data.journalAreaOrder)) {
            const allIds = JOURNAL_AREAS.map((a) => a.id);
            const valid = data.journalAreaOrder.filter((id) =>
              allIds.includes(id),
            );
            const missing = allIds.filter((id) => !valid.includes(id));
            setJournalAreaOrder([...valid, ...missing]);
          }

          // Load no-drinking quit date if set
          if (typeof data.noDrinkingQuitDate === "string") {
            setNoDrinkingQuitDate(data.noDrinkingQuitDate);
          }

          const defaultContractConfig = buildDefaultContractConfig();
          if (data.contractConfig && typeof data.contractConfig === "object") {
            const normalizedTotalWeeks = Number.isFinite(
              Number(data.contractConfig.totalWeeks),
            )
              ? Math.max(1, Number(data.contractConfig.totalWeeks))
              : defaultContractConfig.totalWeeks;
            setContractConfig({
              ...defaultContractConfig,
              ...data.contractConfig,
              quarterStart: normalizeContractStartKey(
                data.contractConfig.quarterStart,
              ),
              amountOwed: Number.isFinite(
                Number(data.contractConfig.amountOwed),
              )
                ? Number(data.contractConfig.amountOwed)
                : defaultContractConfig.amountOwed,
              totalWeeks: normalizedTotalWeeks,
              requiredPassingWeeks: normalizedTotalWeeks,
            });
          } else {
            setContractConfig(defaultContractConfig);
          }

          if (
            data.contractWeekLogs &&
            typeof data.contractWeekLogs === "object"
          ) {
            setContractWeekLogs(data.contractWeekLogs);
          } else {
            setContractWeekLogs({});
          }

          setContractFinalizedAt(
            Number.isFinite(Number(data.contractFinalizedAt))
              ? Number(data.contractFinalizedAt)
              : null,
          );

          // Load net worth fields if they exist
          setPortfolioValue(
            Number.isFinite(data.portfolioValue)
              ? String(data.portfolioValue)
              : "",
          );
          setCheckingValue(
            Number.isFinite(data.checkingValue)
              ? String(data.checkingValue)
              : "",
          );
          setHouseValue(
            Number.isFinite(data.houseValue) ? String(data.houseValue) : "",
          );
          setMortgageDebtValue(
            Number.isFinite(data.mortgageDebtValue)
              ? String(data.mortgageDebtValue)
              : "",
          );
          setRav4DebtValue(
            Number.isFinite(data.rav4DebtValue)
              ? String(data.rav4DebtValue)
              : "",
          );
          setMercedesDebtValue(
            Number.isFinite(data.mercedesDebtValue)
              ? String(data.mercedesDebtValue)
              : "",
          );
          setCreditCardDebtValue(
            Number.isFinite(data.creditCardDebtValue)
              ? String(data.creditCardDebtValue)
              : "",
          );

          const defaultMajorOutcomeSettings =
            buildDefaultMajorOutcomeSettings();
          if (
            data.majorOutcomeSettings &&
            typeof data.majorOutcomeSettings === "object"
          ) {
            const validGoalIds = new Set(
              userGoalsConfig.map((goal) => goal.id),
            );
            const mergedSettings = { ...defaultMajorOutcomeSettings };
            MAJOR_OUTCOMES.forEach((outcome) => {
              const stored = data.majorOutcomeSettings[outcome.id];
              if (!stored || typeof stored !== "object") return;

              mergedSettings[outcome.id] = {
                label:
                  typeof stored.label === "string" && stored.label.trim()
                    ? stored.label
                    : outcome.label,
                icon:
                  typeof stored.icon === "string" && stored.icon.trim()
                    ? stored.icon
                    : outcome.icon,
                targetType:
                  stored.targetType === "house-equity-percent" ||
                  stored.targetType === "weight-loss" ||
                  stored.targetType === "linked-goals-average" ||
                  stored.targetType === "sobriety-days"
                    ? stored.targetType
                    : outcome.targetType,
                targetValue:
                  Number.isFinite(Number(stored.targetValue)) &&
                  Number(stored.targetValue) > 0
                    ? Number(stored.targetValue)
                    : outcome.targetValue,
                linkedGoalIds: Array.isArray(stored.linkedGoalIds)
                  ? stored.linkedGoalIds.filter((id) => validGoalIds.has(id))
                  : [...outcome.linkedGoalIds],
              };
            });
            setMajorOutcomeSettings(mergedSettings);
          } else {
            setMajorOutcomeSettings(defaultMajorOutcomeSettings);
          }

          // Load goal order — migrate from old goalOrder to front/back burner
          const currentGoalIds = userGoalsConfig.map((g) => g.id);

          if (data.frontBurner && Array.isArray(data.frontBurner)) {
            // New format: front/back burner
            const validFront = data.frontBurner.filter((id) =>
              currentGoalIds.includes(id),
            );
            const validBack = (data.backBurner || []).filter((id) =>
              currentGoalIds.includes(id),
            );
            const placed = new Set([...validFront, ...validBack]);
            const newIds = currentGoalIds.filter((id) => !placed.has(id));
            const partitionedNewIds = partitionGoalIds(
              newIds,
              DEFAULT_BACK_BURNER_GOAL_IDS,
            );
            const nextFront = [...validFront, ...partitionedNewIds.front];
            const nextBack = [...validBack, ...partitionedNewIds.back];
            setFrontBurner(nextFront);
            setBackBurner(nextBack);
            setGoalOrder([...nextFront, ...nextBack]);
          } else if (data.goalOrder && Array.isArray(data.goalOrder)) {
            // Legacy migration: put all in front burner
            const validOrderIds = data.goalOrder.filter((id) =>
              currentGoalIds.includes(id),
            );
            const newGoalIds = currentGoalIds.filter(
              (id) => !validOrderIds.includes(id),
            );
            const all = [...validOrderIds, ...newGoalIds];
            const partitionedGoals = partitionGoalIds(
              all,
              DEFAULT_BACK_BURNER_GOAL_IDS,
            );
            setFrontBurner(partitionedGoals.front);
            setBackBurner(partitionedGoals.back);
            setGoalOrder([...partitionedGoals.front, ...partitionedGoals.back]);
          } else {
            const all = userGoalsConfig.map((g) => g.id);
            const partitionedGoals = partitionGoalIds(
              all,
              DEFAULT_BACK_BURNER_GOAL_IDS,
            );
            setFrontBurner(partitionedGoals.front);
            setBackBurner(partitionedGoals.back);
            setGoalOrder([...partitionedGoals.front, ...partitionedGoals.back]);
          }
        } else {
          // Initialize with defaults
          setContractConfig(buildDefaultContractConfig());
          setContractWeekLogs({});
          setContractFinalizedAt(null);

          const initialProgress = {};
          userGoalsConfig.forEach((goal) => {
            if (goal.type === "weight") {
              initialProgress[goal.id] = { current: goal.startValue };
            } else if (goal.type === "checkbox") {
              initialProgress[goal.id] = { completed: false };
            } else if (goal.type === "milestones") {
              initialProgress[goal.id] = { completedMilestones: [] };
            } else if (goal.type === "tapering") {
              initialProgress[goal.id] = {
                currentPhase: 0,
                startDate: null,
                dailyCount: 0,
              };
            } else if (goal.type === "quit-countdown") {
              initialProgress[goal.id] = {
                completedMilestones: [],
                dailyPuffCount: 0,
              };
            } else if (goal.type === "sobriety") {
              initialProgress[goal.id] = {
                startDate: goal.startDate || null,
              };
            } else if (goal.type === "weekly-habit") {
              initialProgress[goal.id] = {
                weekCount: 0,
                weekStart: getCurrentWeekStart(),
              };
            } else if (goal.type === "weekly-dual-count") {
              initialProgress[goal.id] = {
                weekStart: getCurrentWeekStart(),
                current: 0,
                secondaryCurrent: 0,
              };
            } else if (goal.type === "quit-date-streak") {
              initialProgress[goal.id] = {
                quitByDate: "",
                startedDateKey: "",
              };
            } else if (goal.type === "weekly-tracker") {
              initialProgress[goal.id] = { weeks: [], currentWeekCount: 0 };
            } else if (goal.type === "streak") {
              initialProgress[goal.id] = { current: 0, weekProgress: 0 };
            } else if (goal.startValue !== undefined) {
              initialProgress[goal.id] = { current: goal.startValue };
            } else {
              initialProgress[goal.id] = { current: 0 };
            }
          });
          setProgress(initialProgress);
          const all = userGoalsConfig.map((g) => g.id);
          const partitionedGoals = partitionGoalIds(
            all,
            DEFAULT_BACK_BURNER_GOAL_IDS,
          );
          setFrontBurner(partitionedGoals.front);
          setBackBurner(partitionedGoals.back);
          setGoalOrder([...partitionedGoals.front, ...partitionedGoals.back]);
        }
      } catch (error) {
        console.error("Error loading progress:", error);
        setMessage("Error loading progress");
      } finally {
        setLoading(false);
      }
    };

    loadProgress();
  }, [isAuthorized, currentUser, userGoalsConfig, partitionGoalIds]);

  // Get the Monday of the current week as YYYY-MM-DD
  const getCurrentWeekStart = () => {
    return getCurrentWeekStartKey(new Date());
  };

  // Calculate percentage for a goal
  const calculatePercentage = useCallback(
    (goal) => {
      const data = progress[goal.id] || {};

      switch (goal.type) {
        case "weight": {
          const current = data.current || goal.startValue;
          const lost = goal.startValue - current;
          const target = goal.startValue - goal.targetValue;
          return Math.min(Math.max((lost / target) * 100, 0), 100);
        }

        case "checkbox":
          return data.completed ? 100 : 0;

        case "milestones": {
          const completedMilestonesCalc = data.completedMilestones || [];
          return (
            (completedMilestonesCalc.length / goal.milestones.length) * 100
          );
        }

        case "weekly-habit": {
          const currentWeek = getCurrentWeekStart();
          const habitCount =
            data.weekStart === currentWeek ? data.weekCount || 0 : 0;
          return Math.min((habitCount / goal.targetValue) * 100, 100);
        }

        case "weekly-dual-count": {
          const currentWeek = getCurrentWeekStart();
          const sessions =
            data.weekStart === currentWeek ? Number(data.current || 0) : 0;
          const words =
            data.weekStart === currentWeek
              ? Number(data.secondaryCurrent || 0)
              : 0;
          const sessionPct =
            goal.targetValue > 0 ? (sessions / goal.targetValue) * 100 : 0;
          const wordsPct =
            goal.secondaryTargetValue > 0
              ? (words / goal.secondaryTargetValue) * 100
              : 0;
          return Math.min(Math.max(Math.max(sessionPct, wordsPct), 0), 100);
        }

        case "quit-date-streak": {
          if (!data.startedDateKey) return 0;
          const startedDate = parseDateKeyToDate(data.startedDateKey);
          if (!startedDate) return 0;
          const streakDays = Math.max(
            0,
            Math.floor((Date.now() - startedDate.getTime()) / 86400000),
          );
          return Math.min((streakDays / (goal.targetValue || 90)) * 100, 100);
        }

        case "count":
        case "days":
        case "currency":
        case "weekly-count":
        case "dual-count": {
          const value = data.current || 0;
          return Math.min((value / goal.targetValue) * 100, 100);
        }

        case "streak": {
          // For streak goals, show percentage based on current week progress
          const weekProgress = data.weekProgress || 0;
          const weeklyTarget = goal.weeklyTarget || 1;
          return Math.min((weekProgress / weeklyTarget) * 100, 100);
        }

        case "tapering": {
          const currentPhase = data.currentPhase || 0;
          return (currentPhase / goal.phases.length) * 100;
        }

        case "quit-countdown": {
          const completedMilestones = data.completedMilestones || [];
          return (completedMilestones.length / goal.milestones.length) * 100;
        }

        case "sobriety": {
          // Always 100% once quit date has passed
          const quitDateObj = new Date(goal.quitDate);
          const now = new Date();
          return quitDateObj <= now ? 100 : 0;
        }

        case "weekly-tracker": {
          const weeks = data.weeks || [];
          const completedWeeks = weeks.filter((w) => w.completed).length;
          return Math.min((completedWeeks / goal.weeksToTrack) * 100, 100);
        }

        case "percentage":
        default:
          return data.current || 0;
      }
    },
    [progress],
  );

  // Calculate overall streak percentage for progress bar
  const calculateStreakProgress = (goal) => {
    const data = progress[goal.id] || {};
    const currentStreak = data.current || 0;
    return Math.min((currentStreak / goal.targetValue) * 100, 100);
  };

  const majorOutcomes = (() => {
    const currentWeight =
      weightLog.length > 0
        ? Number(weightLog[weightLog.length - 1]?.weight)
        : null;
    const startingWeight =
      weightLog.length > 0 ? Number(weightLog[0]?.weight) : null;
    const poundsLost =
      Number.isFinite(startingWeight) && Number.isFinite(currentWeight)
        ? Math.max(startingWeight - currentWeight, 0)
        : null;

    const houseEquityPercent =
      netWorthSnapshot.house > 0
        ? Math.max(
            0,
            Math.min(
              ((netWorthSnapshot.house - netWorthSnapshot.mortgageDebt) /
                netWorthSnapshot.house) *
                100,
              100,
            ),
          )
        : null;

    const cannabisGoal = GOALS_CONFIG.find(
      (goal) => goal.id === "cannabis-sobriety",
    );
    const cannabisQuitDateRaw = cannabisGoal?.quitDate;
    const cannabisStartTime = cannabisGoal?.startTime || "00:00";
    const cannabisQuitDate = cannabisQuitDateRaw
      ? new Date(`${cannabisQuitDateRaw}T${cannabisStartTime}:00`)
      : null;
    const cannabisSobrietyDays =
      cannabisQuitDate && !Number.isNaN(cannabisQuitDate.getTime())
        ? Math.max(
            Math.floor((Date.now() - cannabisQuitDate.getTime()) / 86400000),
            0,
          )
        : null;

    return MAJOR_OUTCOMES.filter((outcome) => !outcome.hidden).map(
      (outcome) => {
        const settings = majorOutcomeSettings[outcome.id] || outcome;
        const targetValue = Math.max(1, Number(settings.targetValue) || 1);
        const linkedGoalIds = Array.isArray(settings.linkedGoalIds)
          ? settings.linkedGoalIds
          : outcome.linkedGoalIds;
        const linkedGoals = linkedGoalIds
          .map((goalId) => userGoalsConfig.find((goal) => goal.id === goalId))
          .filter(Boolean);

        if (settings.targetType === "house-equity-percent") {
          return {
            ...outcome,
            label: settings.label,
            icon: settings.icon,
            progressPercent:
              houseEquityPercent === null
                ? null
                : Math.min((houseEquityPercent / targetValue) * 100, 100),
            metricLabel:
              houseEquityPercent === null
                ? "Add home value and mortgage to calculate equity"
                : `${Math.round(houseEquityPercent)}% equity vs ${Math.round(targetValue)}% target`,
            subLabel:
              netWorthSnapshot.house > 0
                ? `${currencyFormatter.format(netWorthSnapshot.mortgageDebt)} left on mortgage`
                : "Add house value and mortgage balance below to track this",
            supportLabel: "Logic",
            supportValue:
              netWorthSnapshot.house > 0
                ? "Progress is based on current home equity percent"
                : "Waiting on home value + mortgage inputs",
            targetType: settings.targetType,
            targetValue,
            linkedGoals,
          };
        }

        if (settings.targetType === "weight-loss") {
          return {
            ...outcome,
            label: settings.label,
            icon: settings.icon,
            progressPercent:
              poundsLost === null
                ? null
                : Math.min((poundsLost / targetValue) * 100, 100),
            metricLabel:
              poundsLost === null
                ? "Need a starting and current weigh-in"
                : `${poundsLost.toFixed(1)} / ${targetValue.toFixed(1)} lbs lost`,
            subLabel:
              currentWeight === null
                ? "Log weight entries to turn this into a real outcome meter"
                : `Current weight: ${currentWeight.toFixed(1)} lbs`,
            supportLabel: "Logic",
            supportValue: "Progress is pounds lost divided by your target loss",
            targetType: settings.targetType,
            targetValue,
            linkedGoals,
          };
        }

        if (settings.targetType === "sobriety-days") {
          return {
            ...outcome,
            label: settings.label,
            icon: settings.icon,
            progressPercent:
              cannabisSobrietyDays === null
                ? null
                : Math.min((cannabisSobrietyDays / targetValue) * 100, 100),
            metricLabel:
              cannabisSobrietyDays === null
                ? "Need a cannabis quit date to calculate streak"
                : `${cannabisSobrietyDays} / ${Math.round(targetValue)} sober days`,
            subLabel: cannabisQuitDateRaw
              ? `Tracking from ${cannabisQuitDateRaw}`
              : "Set cannabis quit date in your goal settings",
            supportLabel: "Logic",
            supportValue: "Progress is sober days divided by target days",
            targetType: settings.targetType,
            targetValue,
            linkedGoals,
          };
        }

        const linkedAveragePercent =
          linkedGoals.length > 0
            ? linkedGoals.reduce(
                (sum, goal) => sum + calculatePercentage(goal),
                0,
              ) / linkedGoals.length
            : null;

        return {
          ...outcome,
          label: settings.label,
          icon: settings.icon,
          progressPercent:
            linkedAveragePercent === null
              ? null
              : Math.min((linkedAveragePercent / targetValue) * 100, 100),
          metricLabel:
            linkedAveragePercent === null
              ? "Add supporting goals to create a signal"
              : `${Math.round(linkedAveragePercent)}% driver average vs ${Math.round(targetValue)}% target`,
          subLabel:
            linkedGoals.length > 0
              ? linkedGoals
                  .map((goal) => {
                    const current = progress[goal.id]?.current;
                    if (typeof current === "number") {
                      return `${goal.name}: ${current}${goal.unit ? ` ${goal.unit}` : ""}`;
                    }
                    return goal.name;
                  })
                  .join(" • ")
              : "Select supporting goals in Tune to define this outcome",
          supportLabel: "Logic",
          supportValue:
            linkedAveragePercent === null
              ? "Waiting on linked goals"
              : "Progress is the average completion across linked goals",
          targetType: settings.targetType,
          targetValue,
          linkedGoals,
        };
      },
    );
  })();

  const dashboardGlanceStats = useMemo(() => {
    const outcomesWithSignal = majorOutcomes.filter(
      (o) => typeof o.progressPercent === "number",
    );
    const outcomesAvg =
      outcomesWithSignal.length > 0
        ? Math.round(
            outcomesWithSignal.reduce((sum, o) => sum + o.progressPercent, 0) /
              outcomesWithSignal.length,
          )
        : null;

    const weekDone = (weeklyGoals.goals || []).filter((g) => g.done).length;
    const weekTotal = (weeklyGoals.goals || []).length;

    const taskDone = (kanban.done || []).length;
    const taskTotal = (kanban.cards || []).length;

    const frontGoals = frontBurner
      .map((id) => userGoalsConfig.find((g) => g.id === id))
      .filter(Boolean);
    const frontAvg =
      frontGoals.length > 0
        ? Math.round(
            frontGoals.reduce(
              (sum, goal) => sum + calculatePercentage(goal),
              0,
            ) / frontGoals.length,
          )
        : null;

    const todayKey = getLocalDateKey(new Date());
    const todayJournalCount = journalEntries.filter(
      (entry) => entry.dateKey === todayKey,
    ).length;

    return {
      outcomesAvg,
      weekDone,
      weekTotal,
      taskDone,
      taskTotal,
      frontAvg,
      todayJournalCount,
      netWorth: netWorthSnapshot.netWorth,
    };
  }, [
    majorOutcomes,
    weeklyGoals,
    kanban,
    frontBurner,
    userGoalsConfig,
    journalEntries,
    netWorthSnapshot,
    calculatePercentage,
  ]);

  const contractSummary = useMemo(() => {
    const quarterStart = normalizeContractStartKey(contractConfig.quarterStart);
    const totalWeeks = Math.max(1, Number(contractConfig.totalWeeks) || 13);
    const requiredPassingWeeks = totalWeeks;
    const weekStarts = buildQuarterWeekStarts(quarterStart, totalWeeks);
    const currentWeekStart = getCurrentWeekStart();
    const bikeData = progress["get-active"] || {};
    const swimData = progress["swim-sessions"] || {};
    const writingData = progress["writing-weekly-accountability"] || {};
    const weedQuitData = progress["weed-quit-contract"] || {};
    const currentWeekBikeRides =
      bikeData.weekStart === currentWeekStart ? bikeData.weekCount || 0 : 0;
    const currentWeekSwims =
      swimData.weekStart === currentWeekStart ? swimData.weekCount || 0 : 0;
    const currentWeekWritingSessions =
      writingData.weekStart === currentWeekStart
        ? Number(writingData.current || 0)
        : 0;
    const currentWeekWritingWords =
      writingData.weekStart === currentWeekStart
        ? Number(writingData.secondaryCurrent || 0)
        : 0;

    const quarterEnd = addDaysToDateKey(quarterStart, totalWeeks * 7 - 1);
    const quitByDateKeyRaw =
      weedQuitData.quitByDate || contractConfig.weedQuitDate || quarterEnd;
    const quitByDateKey =
      quitByDateKeyRaw < quarterStart
        ? quarterStart
        : quitByDateKeyRaw > quarterEnd
          ? quarterEnd
          : quitByDateKeyRaw;
    const quitStartedDateKey = weedQuitData.startedDateKey || "";
    const hasQuitStarted = Boolean(quitStartedDateKey);
    const quitStartedDate = parseDateKeyToDate(quitStartedDateKey);
    const quitStreakDays = quitStartedDate
      ? Math.max(
          0,
          Math.floor((Date.now() - quitStartedDate.getTime()) / 86400000),
        )
      : 0;
    const quitByDate = parseDateKeyToDate(quitByDateKey);

    const thcDateKeys = thcLog
      .filter((entry) => Number(entry.doseMg || 0) > 0)
      .map((entry) => {
        if (entry.dateKey) return entry.dateKey;
        if (entry.timestamp) return getLocalDateKey(new Date(entry.timestamp));
        return null;
      })
      .filter(Boolean);

    const hasThcUseAfterQuitBy =
      quitByDate && thcDateKeys.some((dateKey) => dateKey > quitByDateKey);
    const pastQuitByDate = quitByDate
      ? Date.now() >
        new Date(
          quitByDate.getFullYear(),
          quitByDate.getMonth(),
          quitByDate.getDate(),
          23,
          59,
          59,
          999,
        ).getTime()
      : false;
    const weedClauseLabel = !hasQuitStarted
      ? pastQuitByDate
        ? "Failed (not started)"
        : `Set quit by ${formatDateKeyLabel(quitByDateKey)}`
      : hasThcUseAfterQuitBy
        ? "Failed"
        : pastQuitByDate
          ? `Streak ${quitStreakDays} days`
          : `Tapering to ${formatDateKeyLabel(quitByDateKey)}`;

    const weekRows = weekStarts.map((weekStart) => {
      const saved = contractWeekLogs[weekStart] || {};
      const isCurrentWeek = weekStart === currentWeekStart;
      const isFutureWeek = weekStart > currentWeekStart;
      const isPastWeek = weekStart < currentWeekStart;
      const weekEnd = addDaysToDateKey(weekStart, 6);
      const bikeRides = Number(
        isCurrentWeek ? currentWeekBikeRides : saved.bikeRides || 0,
      );
      const swims = Number(isCurrentWeek ? currentWeekSwims : saved.swims || 0);
      const writingSessions = Number(
        isCurrentWeek ? currentWeekWritingSessions : saved.writingSessions || 0,
      );
      const writingWords = Number(
        isCurrentWeek ? currentWeekWritingWords : saved.writingWords || 0,
      );
      const movementPass =
        bikeRides >= Number(contractConfig.bikeTarget || 0) &&
        swims >= Number(contractConfig.swimTarget || 0);
      const writingPass =
        writingSessions >= Number(contractConfig.writingSessionsTarget || 0) ||
        writingWords >= Number(contractConfig.writingWordsTarget || 0);
      const relapse = thcDateKeys.some(
        (dateKey) =>
          dateKey >= weekStart && dateKey <= weekEnd && dateKey > quitByDateKey,
      );
      const weedPass =
        weekStart <= quitByDateKey ? true : hasQuitStarted && !relapse;
      const weekPass = movementPass && writingPass && weedPass;
      let status = "pending";
      if (weekPass) {
        status = "pass";
      } else if (isPastWeek || relapse) {
        status = "miss";
      } else if (isCurrentWeek) {
        status = "in-progress";
      }
      return {
        weekStart,
        label: getWeekLabelFromWeekStart(weekStart),
        bikeRides,
        swims,
        writingSessions,
        writingWords,
        relapse,
        inTaperWindow: weekStart <= quitByDateKey,
        movementPass,
        writingPass,
        weekPass,
        status,
        isCurrentWeek,
        isFutureWeek,
        isPastWeek,
        captured: Boolean(saved.capturedAt),
      };
    });

    const quarterStartDate = parseDateKeyToDate(quarterStart);
    const currentWeekDate = parseDateKeyToDate(currentWeekStart);
    let elapsedWeeks = 0;
    if (
      quarterStartDate &&
      currentWeekDate &&
      currentWeekDate >= quarterStartDate
    ) {
      elapsedWeeks = Math.min(
        totalWeeks,
        Math.floor(
          (currentWeekDate - quarterStartDate) / (7 * 24 * 60 * 60 * 1000),
        ) + 1,
      );
    }

    const elapsedWeekStarts = weekStarts.slice(0, elapsedWeeks);
    const passingWeeksSoFar = weekRows.filter(
      (row) => elapsedWeekStarts.includes(row.weekStart) && row.weekPass,
    ).length;
    const remainingWeeks = Math.max(totalWeeks - elapsedWeeks, 0);
    const canStillPass =
      !hasThcUseAfterQuitBy &&
      passingWeeksSoFar + remainingWeeks >= requiredPassingWeeks;
    const allWeekPasses = weekRows.filter((row) => row.weekPass).length;
    const quarterComplete =
      elapsedWeeks >= totalWeeks || Boolean(contractFinalizedAt);
    const quarterPassed =
      !hasThcUseAfterQuitBy && allWeekPasses >= requiredPassingWeeks;
    const owesNow = hasThcUseAfterQuitBy || (quarterComplete && !quarterPassed);

    return {
      quarterStart,
      totalWeeks,
      requiredPassingWeeks,
      weekRows,
      currentWeekStart,
      currentWeekBikeRides,
      currentWeekSwims,
      currentWeekWritingSessions,
      currentWeekWritingWords,
      quitByDateKey,
      hasQuitStarted,
      quitStreakDays,
      quarterEnd,
      hasThcUseAfterQuitBy: Boolean(hasThcUseAfterQuitBy),
      weedClauseLabel,
      passingWeeksSoFar,
      remainingWeeks,
      canStillPass,
      allWeekPasses,
      quarterComplete,
      quarterPassed,
      owesNow,
    };
  }, [contractConfig, contractWeekLogs, contractFinalizedAt, progress, thcLog]);

  const updateContractConfig = (field, value) => {
    const nextValue =
      field === "quarterStart" ? normalizeContractStartKey(value) : value;
    setContractConfig((prev) => {
      if (field === "totalWeeks") {
        const normalizedTotalWeeks = Math.max(1, Number(nextValue) || 13);
        return {
          ...prev,
          totalWeeks: normalizedTotalWeeks,
          requiredPassingWeeks: normalizedTotalWeeks,
        };
      }

      return {
        ...prev,
        [field]: nextValue,
      };
    });
    setContractFinalizedAt(null);
  };

  useEffect(() => {
    const currentWeekStart = getCurrentWeekStartKey(new Date());
    const bikeData = progress["get-active"] || {};
    const swimData = progress["swim-sessions"] || {};
    const writingData = progress["writing-weekly-accountability"] || {};
    const bikeRides =
      bikeData.weekStart === currentWeekStart
        ? Number(bikeData.weekCount || 0)
        : 0;
    const swims =
      swimData.weekStart === currentWeekStart
        ? Number(swimData.weekCount || 0)
        : 0;
    const writingSessions =
      writingData.weekStart === currentWeekStart
        ? Number(writingData.current || 0)
        : 0;
    const writingWords =
      writingData.weekStart === currentWeekStart
        ? Number(writingData.secondaryCurrent || 0)
        : 0;

    setContractWeekLogs((prev) => {
      const existing = prev[currentWeekStart] || {};
      if (
        Number(existing.bikeRides || 0) === bikeRides &&
        Number(existing.swims || 0) === swims &&
        Number(existing.writingSessions || 0) === writingSessions &&
        Number(existing.writingWords || 0) === writingWords
      ) {
        return prev;
      }

      return {
        ...prev,
        [currentWeekStart]: {
          ...existing,
          bikeRides,
          swims,
          writingSessions,
          writingWords,
        },
      };
    });
  }, [progress]);

  const finalizeQuarterContract = () => {
    if (
      !window.confirm("Finalize this quarter and lock in the contract score?")
    ) {
      return;
    }
    setContractFinalizedAt(Date.now());
    setMessage("✅ Quarter finalized");
    setTimeout(() => setMessage(""), 2000);
  };

  const updateMajorOutcomeSetting = (outcomeId, updates) => {
    setMajorOutcomeSettings((prev) => ({
      ...prev,
      [outcomeId]: {
        ...(prev[outcomeId] || buildDefaultMajorOutcomeSettings()[outcomeId]),
        ...updates,
      },
    }));
  };

  // Update progress
  const updateProgress = (goalId, value) => {
    setProgress((prev) => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        ...value,
      },
    }));
  };

  // Reset progress for a specific goal
  const resetProgress = (goalId) => {
    const goal = userGoalsConfig.find((g) => g.id === goalId);
    if (!goal) return;

    const confirmed = window.confirm(
      `Are you sure you want to reset progress for "${goal.name}"?`,
    );
    if (!confirmed) return;

    let initialValue;
    if (goal.type === "weight") {
      initialValue = { current: goal.startValue };
    } else if (goal.type === "checkbox") {
      initialValue = { completed: false };
    } else if (goal.type === "milestones") {
      initialValue = { completedMilestones: [] };
    } else if (goal.type === "tapering") {
      initialValue = { currentPhase: 0, startDate: null, dailyCount: 0 };
    } else if (goal.type === "quit-countdown") {
      initialValue = { completedMilestones: [], dailyPuffCount: 0 };
    } else if (goal.type === "weekly-habit") {
      initialValue = { weekCount: 0, weekStart: getCurrentWeekStart() };
    } else if (goal.type === "weekly-dual-count") {
      initialValue = {
        weekStart: getCurrentWeekStart(),
        current: 0,
        secondaryCurrent: 0,
      };
    } else if (goal.type === "quit-date-streak") {
      initialValue = {
        quitByDate: "",
        startedDateKey: "",
      };
    } else if (goal.type === "weekly-tracker") {
      initialValue = { weeks: [], currentWeekCount: 0 };
    } else if (goal.type === "streak") {
      initialValue = { current: 0, weekProgress: 0 };
    } else if (goal.startValue !== undefined) {
      initialValue = { current: goal.startValue };
    } else {
      initialValue = { current: 0 };
    }

    updateProgress(goalId, initialValue);
    setMessage(`🔄 Reset progress for "${goal.name}"`);
    setTimeout(() => setMessage(""), 3000);
  };

  // Calculate current streak for weekly tracker
  const getCurrentStreak = (weeks) => {
    if (!weeks || weeks.length === 0) return 0;

    let streak = 0;
    for (let i = weeks.length - 1; i >= 0; i--) {
      if (weeks[i]?.completed) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  // Toggle checklist visibility for a specific goal
  const toggleChecklistVisibility = (goalId) => {
    setChecklistVisibility((prev) => ({
      ...prev,
      [goalId]: !prev[goalId],
    }));
  };

  // --- Accomplishment log helpers ---
  const [accomplishmentInputs, setAccomplishmentInputs] = useState({});
  const [accomplishmentDateInputs, setAccomplishmentDateInputs] = useState({});

  const addAccomplishment = (goalId) => {
    const text = (accomplishmentInputs[goalId] || "").trim();
    if (!text) return;
    const rawDate = accomplishmentDateInputs[goalId];
    let dateStr;
    let ts;
    if (rawDate) {
      const parsed = new Date(rawDate + "T12:00:00");
      dateStr = parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      ts = parsed.getTime();
    } else {
      dateStr = new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      ts = Date.now();
    }
    const entry = {
      text,
      date: dateStr,
      timestamp: ts,
    };
    setProgress((prev) => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        accomplishments: [...(prev[goalId]?.accomplishments || []), entry],
      },
    }));
    setAccomplishmentInputs((prev) => ({ ...prev, [goalId]: "" }));
    setAccomplishmentDateInputs((prev) => ({ ...prev, [goalId]: "" }));
  };

  const deleteAccomplishment = (goalId, index) => {
    setProgress((prev) => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        accomplishments: (prev[goalId]?.accomplishments || []).filter(
          (_, i) => i !== index,
        ),
      },
    }));
  };

  // --- Ideas list helpers ---
  const [ideaInputs, setIdeaInputs] = useState({});

  const addIdea = (goalId) => {
    const text = (ideaInputs[goalId] || "").trim();
    if (!text) return;
    setProgress((prev) => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        ideas: [...(prev[goalId]?.ideas || []), text],
      },
    }));
    setIdeaInputs((prev) => ({ ...prev, [goalId]: "" }));
  };

  const deleteIdea = (goalId, index) => {
    setProgress((prev) => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        ideas: (prev[goalId]?.ideas || []).filter((_, i) => i !== index),
      },
    }));
  };

  // --- Weight tracking helpers ---
  const addWeightEntry = () => {
    const val = parseFloat(weightInput);
    if (isNaN(val) || val < 50 || val > 500) return;
    const entry = {
      weight: val,
      date: new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      timestamp: Date.now(),
    };
    setWeightLog((prev) => [...prev, entry]);
    setWeightInput("");
  };

  const deleteWeightEntry = (index) => {
    setWeightLog((prev) => prev.filter((_, i) => i !== index));
  };

  const addThcDose = (doseMg) => {
    const now = new Date();
    const entry = {
      doseMg,
      date: formatShortDate(now),
      dateKey: getLocalDateKey(now),
      timestamp: Date.now(),
    };
    setThcLog((prev) => [...prev, entry]);
  };

  // --- Journal helpers ---
  const setJournalDraft = (areaId, text) => {
    setJournalDrafts((prev) => ({ ...prev, [areaId]: text }));
  };

  const addJournalEntry = (areaId) => {
    const text = (journalDrafts[areaId] || "").trim();
    if (!text) return;
    const now = new Date();
    const entry = {
      id: `${areaId}-${Date.now()}`,
      area: areaId,
      text,
      date: formatShortDate(now),
      dateKey: getLocalDateKey(now),
      timestamp: Date.now(),
    };
    setJournalEntries((prev) => [...prev, entry]);
    setJournalDrafts((prev) => ({ ...prev, [areaId]: "" }));
  };

  const deleteJournalEntry = (id) => {
    setJournalEntries((prev) => prev.filter((e) => e.id !== id));
    if (editingJournalId === id) {
      setEditingJournalId(null);
      setEditingJournalText("");
    }
  };

  const startEditJournalEntry = (entry) => {
    setEditingJournalId(entry.id);
    setEditingJournalText(entry.text);
  };

  const cancelEditJournalEntry = () => {
    setEditingJournalId(null);
    setEditingJournalText("");
  };

  const saveEditJournalEntry = () => {
    const trimmed = editingJournalText.trim();
    if (!editingJournalId) return;
    if (!trimmed) {
      // Empty edit deletes the entry
      deleteJournalEntry(editingJournalId);
      return;
    }
    setJournalEntries((prev) =>
      prev.map((e) =>
        e.id === editingJournalId
          ? { ...e, text: trimmed, editedAt: Date.now() }
          : e,
      ),
    );
    setEditingJournalId(null);
    setEditingJournalText("");
  };

  // Draw weight graph on canvas
  useEffect(() => {
    if (rightPanel !== "weight") return;
    const canvas = weightCanvasRef.current;
    if (!canvas || weightLog.length === 0) return;

    const frame = requestAnimationFrame(() => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);

      const weights = weightLog.map((e) => e.weight);
      const minW = Math.min(...weights) - 2;
      const maxW = Math.max(...weights) + 2;
      const range = maxW - minW || 1;
      const padL = 38,
        padR = 10,
        padT = 12,
        padB = 22;
      const plotW = w - padL - padR;
      const plotH = h - padT - padB;

      // Grid lines
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.lineWidth = 1;
      const gridCount = 4;
      for (let i = 0; i <= gridCount; i++) {
        const y = padT + (plotH / gridCount) * i;
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(w - padR, y);
        ctx.stroke();
        // Label
        ctx.fillStyle = "rgba(255,255,255,0.4)";
        ctx.font = "10px Georgia";
        ctx.textAlign = "right";
        const label = (maxW - (range / gridCount) * i).toFixed(0);
        ctx.fillText(label, padL - 4, y + 3);
      }

      // Line
      ctx.strokeStyle = "#4fc3f7";
      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.beginPath();
      const points = weights.map((wt, i) => ({
        x: padL + (plotW / Math.max(weights.length - 1, 1)) * i,
        y: padT + plotH - ((wt - minW) / range) * plotH,
      }));
      points.forEach((p, i) =>
        i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y),
      );
      ctx.stroke();

      // Dots
      points.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = "#4fc3f7";
        ctx.fill();
      });

      // Date labels (first, last)
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "9px Georgia";
      ctx.textAlign = "left";
      ctx.fillText(weightLog[0].date, padL, h - 2);
      if (weightLog.length > 1) {
        ctx.textAlign = "right";
        ctx.fillText(weightLog[weightLog.length - 1].date, w - padR, h - 2);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [weightLog, rightPanel]);

  // Draw THC daily totals graph on canvas
  useEffect(() => {
    if (rightPanel !== "thc") return;
    const canvas = thcCanvasRef.current;
    if (!canvas || thcDailyTotals.length === 0) return;

    const frame = requestAnimationFrame(() => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);

      const totals = thcDailyTotals.map((d) => d.totalMg);
      const maxTotal = Math.max(...totals, 10);
      const padL = 38,
        padR = 10,
        padT = 12,
        padB = 22;
      const plotW = w - padL - padR;
      const plotH = h - padT - padB;

      // Grid lines
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.lineWidth = 1;
      const gridCount = 4;
      for (let i = 0; i <= gridCount; i++) {
        const y = padT + (plotH / gridCount) * i;
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(w - padR, y);
        ctx.stroke();
        ctx.fillStyle = "rgba(255,255,255,0.4)";
        ctx.font = "10px Georgia";
        ctx.textAlign = "right";
        const label = ((maxTotal / gridCount) * (gridCount - i)).toFixed(1);
        ctx.fillText(label, padL - 4, y + 3);
      }

      // Bars
      const barSlot = plotW / thcDailyTotals.length;
      const barW = Math.max(5, barSlot * 0.6);
      thcDailyTotals.forEach((day, i) => {
        const barH = (day.totalMg / maxTotal) * plotH;
        const x = padL + i * barSlot + (barSlot - barW) / 2;
        const y = padT + plotH - barH;
        ctx.fillStyle = "rgba(74, 222, 128, 0.85)";
        ctx.fillRect(x, y, barW, barH);
      });

      // Date labels (first, last)
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "9px Georgia";
      ctx.textAlign = "left";
      ctx.fillText(thcDailyTotals[0].dateLabel, padL, h - 2);
      if (thcDailyTotals.length > 1) {
        ctx.textAlign = "right";
        ctx.fillText(
          thcDailyTotals[thcDailyTotals.length - 1].dateLabel,
          w - padR,
          h - 2,
        );
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [thcDailyTotals, rightPanel]);

  // --- Subtask / checklist helpers (shared by both kanbans) ---
  const toggleExpandCard = (cardId) => {
    setExpandedKanbanCards((prev) => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const updateCardInKanban = (setter, cardId, updater) => {
    setter((prev) => ({
      ...prev,
      cards: prev.cards.map((c) => (c.id === cardId ? updater(c) : c)),
    }));
  };

  const addSubtask = (setter, cardId) => {
    const text = (subtaskInputs[cardId] || "").trim();
    if (!text) return;
    updateCardInKanban(setter, cardId, (card) => ({
      ...card,
      checklist: [...(card.checklist || []), { text, done: false }],
    }));
    setSubtaskInputs((prev) => ({ ...prev, [cardId]: "" }));
  };

  const toggleSubtask = (setter, cardId, index) => {
    updateCardInKanban(setter, cardId, (card) => ({
      ...card,
      checklist: (card.checklist || []).map((item, i) =>
        i === index ? { ...item, done: !item.done } : item,
      ),
    }));
  };

  const deleteSubtask = (setter, cardId, index) => {
    updateCardInKanban(setter, cardId, (card) => ({
      ...card,
      checklist: (card.checklist || []).filter((_, i) => i !== index),
    }));
  };

  // --- Kanban helpers ---
  const findCardColumn = (cardId) => {
    for (const col of KANBAN_COLUMNS) {
      if (kanban[col]?.includes(cardId)) return col;
    }
    return null;
  };

  const addKanbanCard = () => {
    const text = newTaskText.trim();
    if (!text) return;
    const id = `task-${kanban.nextId}`;
    setKanban((prev) => ({
      ...prev,
      cards: [...prev.cards, { id, text, checklist: [] }],
      todo: [...prev.todo, id],
      nextId: prev.nextId + 1,
    }));
    setNewTaskText("");
  };

  const deleteKanbanCard = (cardId) => {
    setKanban((prev) => ({
      ...prev,
      cards: prev.cards.filter((c) => c.id !== cardId),
      done: prev.done.filter((id) => id !== cardId),
    }));
  };

  const archiveKanbanCard = (cardId) => {
    const card = kanban.cards.find((c) => c.id === cardId);
    if (!card) return;
    setKanban((prev) => ({
      ...prev,
      cards: prev.cards.filter((c) => c.id !== cardId),
      done: prev.done.filter((id) => id !== cardId),
    }));
    setKanbanArchive((prev) => [{ ...card, archivedAt: Date.now() }, ...prev]);
  };

  const unarchiveKanbanCard = (cardId) => {
    const card = kanbanArchive.find((c) => c.id === cardId);
    if (!card) return;
    const { archivedAt: _a, ...rest } = card;
    setKanbanArchive((prev) => prev.filter((c) => c.id !== cardId));
    setKanban((prev) => ({
      ...prev,
      cards: [...prev.cards, rest],
      done: [...prev.done, rest.id],
    }));
  };

  const deleteFromKanbanArchive = (cardId) => {
    setKanbanArchive((prev) => prev.filter((c) => c.id !== cardId));
  };

  const moveCardToColumn = (cardId, targetCol) => {
    const sourceCol = findCardColumn(cardId);
    if (!sourceCol || sourceCol === targetCol) return;
    setKanban((prev) => ({
      ...prev,
      [sourceCol]: prev[sourceCol].filter((id) => id !== cardId),
      [targetCol]: [...prev[targetCol], cardId],
    }));
  };

  const handleKanbanDragStart = (event) => {
    setDraggedTaskId(event.active.id);
  };

  const handleKanbanDragEnd = (event) => {
    setDraggedTaskId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Dropped on a column header (droppable)
    if (KANBAN_COLUMNS.includes(overId)) {
      moveCardToColumn(activeId, overId);
      return;
    }

    // Dropped on another card
    const sourceCol = findCardColumn(activeId);
    const targetCol = findCardColumn(overId);
    if (!sourceCol || !targetCol) return;

    if (sourceCol === targetCol) {
      // Reorder within column
      const col = [...kanban[sourceCol]];
      const oldIdx = col.indexOf(activeId);
      const newIdx = col.indexOf(overId);
      setKanban((prev) => ({
        ...prev,
        [sourceCol]: arrayMove(col, oldIdx, newIdx),
      }));
    } else {
      // Move between columns, place at target card's position
      const targetList = [...kanban[targetCol]];
      const insertIdx = targetList.indexOf(overId);
      setKanban((prev) => ({
        ...prev,
        [sourceCol]: prev[sourceCol].filter((id) => id !== activeId),
        [targetCol]: [
          ...targetList.slice(0, insertIdx),
          activeId,
          ...targetList.slice(insertIdx),
        ],
      }));
    }
  };

  // --- Hobby Kanban helpers ---
  const findHobbyCardColumn = (cardId) => {
    for (const col of KANBAN_COLUMNS) {
      if (hobbyKanban[col]?.includes(cardId)) return col;
    }
    return null;
  };

  const addHobbyCard = () => {
    const text = newHobbyText.trim();
    if (!text) return;
    const id = `hobby-${hobbyKanban.nextId}`;
    const subtitle = newHobbySubtitle.trim();
    setHobbyKanban((prev) => ({
      ...prev,
      cards: [
        ...prev.cards,
        { id, text, subtitle: subtitle || "", checklist: [] },
      ],
      todo: [...prev.todo, id],
      nextId: prev.nextId + 1,
    }));
    setNewHobbyText("");
    setNewHobbySubtitle("");
  };

  const deleteHobbyCard = (cardId) => {
    setHobbyKanban((prev) => ({
      ...prev,
      cards: prev.cards.filter((c) => c.id !== cardId),
      done: prev.done.filter((id) => id !== cardId),
    }));
  };

  const archiveHobbyCard = (cardId) => {
    const card = hobbyKanban.cards.find((c) => c.id === cardId);
    if (!card) return;
    setHobbyKanban((prev) => ({
      ...prev,
      cards: prev.cards.filter((c) => c.id !== cardId),
      done: prev.done.filter((id) => id !== cardId),
    }));
    setHobbyKanbanArchive((prev) => [
      { ...card, archivedAt: Date.now() },
      ...prev,
    ]);
  };

  const unarchiveHobbyCard = (cardId) => {
    const card = hobbyKanbanArchive.find((c) => c.id === cardId);
    if (!card) return;
    const { archivedAt: _a, ...rest } = card;
    setHobbyKanbanArchive((prev) => prev.filter((c) => c.id !== cardId));
    setHobbyKanban((prev) => ({
      ...prev,
      cards: [...prev.cards, rest],
      done: [...prev.done, rest.id],
    }));
  };

  const deleteFromHobbyArchive = (cardId) => {
    setHobbyKanbanArchive((prev) => prev.filter((c) => c.id !== cardId));
  };

  const moveHobbyCardToColumn = (cardId, targetCol) => {
    const sourceCol = findHobbyCardColumn(cardId);
    if (!sourceCol || sourceCol === targetCol) return;
    setHobbyKanban((prev) => ({
      ...prev,
      [sourceCol]: prev[sourceCol].filter((id) => id !== cardId),
      [targetCol]: [...prev[targetCol], cardId],
    }));
  };

  const handleHobbyDragStart = (event) => {
    setDraggedHobbyId(event.active.id);
  };

  const handleHobbyDragEnd = (event) => {
    setDraggedHobbyId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (KANBAN_COLUMNS.includes(overId)) {
      moveHobbyCardToColumn(activeId, overId);
      return;
    }

    const sourceCol = findHobbyCardColumn(activeId);
    const targetCol = findHobbyCardColumn(overId);
    if (!sourceCol || !targetCol) return;

    if (sourceCol === targetCol) {
      const col = [...hobbyKanban[sourceCol]];
      const oldIdx = col.indexOf(activeId);
      const newIdx = col.indexOf(overId);
      setHobbyKanban((prev) => ({
        ...prev,
        [sourceCol]: arrayMove(col, oldIdx, newIdx),
      }));
    } else {
      const targetList = [...hobbyKanban[targetCol]];
      const insertIdx = targetList.indexOf(overId);
      setHobbyKanban((prev) => ({
        ...prev,
        [sourceCol]: prev[sourceCol].filter((id) => id !== activeId),
        [targetCol]: [
          ...targetList.slice(0, insertIdx),
          activeId,
          ...targetList.slice(insertIdx),
        ],
      }));
    }
  };

  // --- Weekly Goals helpers ---
  const addWeeklyGoal = () => {
    const text = newWeeklyGoalText.trim();
    if (!text) return;
    const currentWeekStart = getCurrentWeekStart();
    setWeeklyGoals((prev) => ({
      ...prev,
      weekStart: prev.weekStart || currentWeekStart,
      goals: [...prev.goals, { id: `wg-${prev.nextId}`, text, done: false }],
      nextId: prev.nextId + 1,
    }));
    setNewWeeklyGoalText("");
  };

  const toggleWeeklyGoal = (id) => {
    setWeeklyGoals((prev) => ({
      ...prev,
      goals: prev.goals.map((g) => (g.id === id ? { ...g, done: !g.done } : g)),
    }));
  };

  const deleteWeeklyGoal = (id) => {
    setWeeklyGoals((prev) => ({
      ...prev,
      goals: prev.goals.filter((g) => g.id !== id),
    }));
  };

  const endWeek = () => {
    const doneGoals = weeklyGoals.goals.filter((g) => g.done);
    if (doneGoals.length === 0 && weeklyGoals.goals.length === 0) return;

    const currentWeekStart = weeklyGoals.weekStart || getCurrentWeekStart();
    if (doneGoals.length > 0) {
      const archiveEntry = {
        weekStart: currentWeekStart,
        weekLabel: getWeekLabelFromWeekStart(currentWeekStart),
        accomplished: doneGoals.map((g) => g.text),
        archivedAt: Date.now(),
      };
      setWeeklyGoalsArchive((prev) =>
        upsertWeeklyArchiveEntry(prev, archiveEntry),
      );
    }

    const nextWeekStart = getCurrentWeekStart();
    setWeeklyGoals((prev) => ({
      ...prev,
      weekStart: nextWeekStart,
      goals: [],
    }));
  };

  // Save to Firestore
  const handleSave = useCallback(async () => {
    if (!currentUser) return;
    setSaving(true);

    try {
      const docRef = doc(db, "goals", currentUser);
      await setDoc(docRef, {
        ...progress,
        goalOrder,
        frontBurner,
        backBurner,
        kanban,
        kanbanArchive,
        hobbyKanban,
        hobbyKanbanArchive,
        weeklyGoals,
        weeklyGoalsArchive,
        weightLog,
        thcLog,
        journalEntries,
        journalAreaOrder,
        isJournalCollapsed,
        majorOutcomeSettings,
        noDrinkingQuitDate,
        contractConfig,
        contractWeekLogs,
        contractFinalizedAt,
        portfolioValue: parseFinanceValue(portfolioValue),
        checkingValue: parseFinanceValue(checkingValue),
        houseValue: parseFinanceValue(houseValue),
        mortgageDebtValue: Math.abs(parseFinanceValue(mortgageDebtValue)),
        rav4DebtValue: Math.abs(parseFinanceValue(rav4DebtValue)),
        mercedesDebtValue: Math.abs(parseFinanceValue(mercedesDebtValue)),
        creditCardDebtValue: Math.abs(parseFinanceValue(creditCardDebtValue)),
      });
      setMessage("✅ Saved");
      setTimeout(() => setMessage(""), 2000);
    } catch (error) {
      console.error("Error saving progress:", error);
      setMessage("❌ Error saving progress");
    } finally {
      setSaving(false);
    }
  }, [
    currentUser,
    progress,
    goalOrder,
    frontBurner,
    backBurner,
    kanban,
    kanbanArchive,
    hobbyKanban,
    hobbyKanbanArchive,
    weeklyGoals,
    weeklyGoalsArchive,
    weightLog,
    thcLog,
    journalEntries,
    journalAreaOrder,
    isJournalCollapsed,
    majorOutcomeSettings,
    noDrinkingQuitDate,
    contractConfig,
    contractWeekLogs,
    contractFinalizedAt,
    portfolioValue,
    checkingValue,
    houseValue,
    mortgageDebtValue,
    rav4DebtValue,
    mercedesDebtValue,
    creditCardDebtValue,
    parseFinanceValue,
  ]);

  // Auto-save with debounce — waits 1.5s after last change
  const hasLoadedRef = useRef(false);
  const autoSaveTimerRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    // Skip the first render after data loads
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      return;
    }
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSave();
    }, 1500);
    return () => clearTimeout(autoSaveTimerRef.current);
  }, [
    progress,
    goalOrder,
    frontBurner,
    backBurner,
    kanban,
    kanbanArchive,
    hobbyKanban,
    hobbyKanbanArchive,
    weeklyGoals,
    weeklyGoalsArchive,
    weightLog,
    thcLog,
    journalEntries,
    journalAreaOrder,
    isJournalCollapsed,
    majorOutcomeSettings,
    noDrinkingQuitDate,
    contractConfig,
    contractWeekLogs,
    contractFinalizedAt,
    portfolioValue,
    checkingValue,
    houseValue,
    mortgageDebtValue,
    rav4DebtValue,
    mercedesDebtValue,
    creditCardDebtValue,
    loading,
    handleSave,
  ]);

  // Handle drag end — supports reordering within and moving between front/back burner
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const inFront = frontBurner.includes(activeId);
    const inBack = backBurner.includes(activeId);

    // Determine target container
    let targetIsFront;
    if (overId === "front-burner-drop" || frontBurner.includes(overId)) {
      targetIsFront = true;
    } else if (overId === "back-burner-drop" || backBurner.includes(overId)) {
      targetIsFront = false;
    } else {
      return;
    }

    if (inFront && targetIsFront) {
      // Reorder within front burner
      if (activeId !== overId) {
        setFrontBurner((items) =>
          arrayMove(items, items.indexOf(activeId), items.indexOf(overId)),
        );
      }
    } else if (inBack && !targetIsFront) {
      // Reorder within back burner
      if (activeId !== overId) {
        setBackBurner((items) =>
          arrayMove(items, items.indexOf(activeId), items.indexOf(overId)),
        );
      }
    } else if (inFront && !targetIsFront) {
      // Move from front → back
      setFrontBurner((items) => items.filter((id) => id !== activeId));
      setBackBurner((items) => {
        const idx = items.indexOf(overId);
        const insertAt = idx >= 0 ? idx : items.length;
        const next = [...items];
        next.splice(insertAt, 0, activeId);
        return next;
      });
    } else if (inBack && targetIsFront) {
      // Move from back → front
      setBackBurner((items) => items.filter((id) => id !== activeId));
      setFrontBurner((items) => {
        const idx = items.indexOf(overId);
        const insertAt = idx >= 0 ? idx : items.length;
        const next = [...items];
        next.splice(insertAt, 0, activeId);
        return next;
      });
    }

    // Keep goalOrder in sync (done via useEffect below)
  };

  // Keep goalOrder in sync with front/back burner
  useEffect(() => {
    setGoalOrder([...frontBurner, ...backBurner]);
  }, [frontBurner, backBurner]);

  // Don't render anything if not authorized
  if (!isAuthorized) {
    return null;
  }

  if (loading) {
    return (
      <div className="goals-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Render checklist for a goal
  const renderChecklist = (goal) => {
    const data = progress[goal.id] || {};
    const checklist = data.checklist || [];

    const addChecklistItem = (inputElement) => {
      const text = inputElement.value.trim();
      if (text) {
        const newChecklist = [
          ...checklist,
          { id: Date.now(), text: text, completed: false },
        ];
        updateProgress(goal.id, { ...data, checklist: newChecklist });
        inputElement.value = "";
      }
    };

    const toggleChecklistItem = (itemId) => {
      const newChecklist = checklist.map((item) =>
        item.id === itemId ? { ...item, completed: !item.completed } : item,
      );
      updateProgress(goal.id, { ...data, checklist: newChecklist });
    };

    const removeChecklistItem = (itemId) => {
      const newChecklist = checklist.filter((item) => item.id !== itemId);
      updateProgress(goal.id, { ...data, checklist: newChecklist });
    };

    return (
      <div className="goal-checklist">
        <div className="checklist-items">
          {checklist.map((item) => (
            <div key={item.id} className="checklist-item">
              <input
                type="checkbox"
                checked={item.completed}
                onChange={() => toggleChecklistItem(item.id)}
                className="checklist-checkbox"
              />
              <span
                className={`checklist-text ${item.completed ? "completed" : ""}`}
              >
                {item.text}
              </span>
              <button
                className="checklist-remove"
                onClick={() => removeChecklistItem(item.id)}
                title="Remove item"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="checklist-add">
          <input
            type="text"
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                addChecklistItem(e.target);
              }
            }}
            placeholder="Add a task..."
            className="checklist-input"
            id={`checklist-input-${goal.id}`}
          />
          <button
            className="checklist-add-button"
            onClick={() => {
              const input = document.getElementById(
                `checklist-input-${goal.id}`,
              );
              addChecklistItem(input);
            }}
          >
            +
          </button>
        </div>
      </div>
    );
  };

  // Render different input types based on goal configuration
  const renderGoalInput = (goal) => {
    const data = progress[goal.id] || {};

    switch (goal.type) {
      case "checkbox":
        return (
          <div className="checkbox-input">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={data.completed || false}
                onChange={(e) =>
                  updateProgress(goal.id, { completed: e.target.checked })
                }
                className="goal-checkbox"
              />
              <span className="checkbox-text">
                {data.completed ? "✅ Completed!" : "⬜ Not yet completed"}
              </span>
            </label>
          </div>
        );

      case "weight":
        return (
          <div className="number-input">
            <label className="input-label">
              Current Weight:
              <input
                type="number"
                value={data.current || goal.startValue}
                onChange={(e) =>
                  updateProgress(goal.id, {
                    current: parseFloat(e.target.value),
                  })
                }
                className="number-field"
                step="0.5"
                min={goal.targetValue}
                max={goal.startValue}
              />
              <span className="unit">{goal.unit}</span>
            </label>
            <div className="target-info">
              Target: {goal.targetValue} {goal.unit} (
              {(goal.startValue - (data.current || goal.startValue)).toFixed(1)}{" "}
              lbs to go)
            </div>
          </div>
        );

      case "weekly-habit": {
        const currentWeek = getCurrentWeekStart();
        const weekCount =
          data.weekStart === currentWeek ? data.weekCount || 0 : 0;
        const weekTarget = goal.targetValue;

        const setWeekCount = (newCount) => {
          updateProgress(goal.id, {
            weekCount: Math.min(Math.max(newCount, 0), weekTarget),
            weekStart: currentWeek,
          });
        };

        return (
          <div className="weekly-habit-input">
            <div className="weekly-habit-header">
              <span className="weekly-habit-label">This Week</span>
              <span className="weekly-habit-count">
                {weekCount} / {weekTarget}
              </span>
            </div>
            <div className="weekly-habit-dots">
              {Array.from({ length: weekTarget }).map((_, i) => (
                <button
                  key={i}
                  className={`weekly-habit-dot ${i < weekCount ? "completed" : ""}`}
                  onClick={() => setWeekCount(i < weekCount ? i : i + 1)}
                  title={`Session ${i + 1}`}
                />
              ))}
            </div>
            <div className="weekly-habit-actions">
              <button
                className="weekly-habit-log-button"
                onClick={() => setWeekCount(weekCount + 1)}
                disabled={weekCount >= weekTarget}
              >
                + Log Session
              </button>
            </div>
            <div className="target-info">
              {weekCount >= weekTarget
                ? "🎉 Weekly target hit!"
                : `${weekTarget - weekCount} more session${weekTarget - weekCount !== 1 ? "s" : ""} to go`}
              {" • Resets every Monday"}
            </div>
          </div>
        );
      }

      case "count":
      case "days":
      case "currency":
      case "weekly-count":
        const prefix = goal.type === "currency" ? "$" : "";
        const isWeekly = goal.type === "weekly-count";
        const isNovelGoal = goal.id === "write-novel";
        return (
          <div className="number-input">
            <label className="input-label">
              {goal.type === "days"
                ? "Days Sober"
                : goal.type === "currency"
                  ? "Amount"
                  : isWeekly
                    ? "This Week"
                    : "Count"}
              :
              <input
                type="number"
                value={data.current || 0}
                onChange={(e) =>
                  updateProgress(goal.id, { current: parseInt(e.target.value) })
                }
                className="number-field"
                min="0"
                max={goal.targetValue}
              />
              <span className="unit">
                {prefix}
                {goal.unit}
              </span>
            </label>
            <div className="target-info">
              Target: {prefix}
              {goal.targetValue.toLocaleString()} {goal.unit}
            </div>
            {isNovelGoal && (
              <div className="target-info">
                Weekly rhythm: {goal.weeklySessionTarget || 3} sessions OR at
                least {(goal.weeklyWordFloor || 1500).toLocaleString()} words.
              </div>
            )}
          </div>
        );

      case "dual-count":
        return (
          <div className="dual-count-input">
            <div className="dual-count-row">
              <div className="number-input">
                <label className="input-label">
                  {goal.unit}:
                  <input
                    type="number"
                    value={data.current || 0}
                    onChange={(e) =>
                      updateProgress(goal.id, {
                        ...data,
                        current: parseInt(e.target.value),
                      })
                    }
                    className="number-field"
                    min="0"
                  />
                  <span className="unit">{goal.unit}</span>
                </label>
                <div className="target-info">
                  Target: {goal.targetValue.toLocaleString()} {goal.unit}
                </div>
              </div>

              <div className="number-input">
                <label className="input-label">
                  Current {goal.secondaryUnit}:
                  <input
                    type="number"
                    value={data.secondaryCurrent || 0}
                    onChange={(e) =>
                      updateProgress(goal.id, {
                        ...data,
                        secondaryCurrent: parseInt(e.target.value),
                      })
                    }
                    className="number-field"
                    min="0"
                  />
                  <span className="unit">{goal.secondaryUnit}</span>
                </label>
                <div className="target-info">
                  {goal.secondaryTarget} (
                  {goal.secondaryTargetValue.toLocaleString()})
                </div>
              </div>
            </div>
          </div>
        );

      case "weekly-dual-count": {
        const currentWeek = getCurrentWeekStart();
        const sessions =
          data.weekStart === currentWeek ? Number(data.current || 0) : 0;
        const words =
          data.weekStart === currentWeek
            ? Number(data.secondaryCurrent || 0)
            : 0;
        const sessionTarget = Number(goal.targetValue || 0);
        const wordsTarget = Number(goal.secondaryTargetValue || 0);

        const setWeeklyWriting = (sessionsValue, wordsValue) => {
          updateProgress(goal.id, {
            ...data,
            weekStart: currentWeek,
            current: Math.max(0, sessionsValue),
            secondaryCurrent: Math.max(0, wordsValue),
          });
        };

        return (
          <div className="dual-count-input">
            <div className="dual-count-row">
              <div className="number-input">
                <label className="input-label">
                  Sessions this week:
                  <input
                    type="number"
                    value={sessions}
                    onChange={(e) =>
                      setWeeklyWriting(parseInt(e.target.value, 10) || 0, words)
                    }
                    className="number-field"
                    min="0"
                  />
                  <span className="unit">sessions</span>
                </label>
                <div className="target-info">
                  Target: {sessionTarget.toLocaleString()} sessions
                </div>
              </div>

              <div className="number-input">
                <label className="input-label">
                  Words this week:
                  <input
                    type="number"
                    value={words}
                    onChange={(e) =>
                      setWeeklyWriting(
                        sessions,
                        parseInt(e.target.value, 10) || 0,
                      )
                    }
                    className="number-field"
                    min="0"
                  />
                  <span className="unit">words</span>
                </label>
                <div className="target-info">
                  Target: {wordsTarget.toLocaleString()} words
                </div>
              </div>
            </div>
            <div className="target-info">
              Weekly pass when either target is hit. Resets every Monday.
            </div>
          </div>
        );
      }

      case "quit-date-streak": {
        const quitByDate = data.quitByDate || "";
        const startedDateKey = data.startedDateKey || "";
        const hasStarted = Boolean(startedDateKey);
        const startedDate = parseDateKeyToDate(startedDateKey);
        const streakDays = startedDate
          ? Math.max(
              0,
              Math.floor((Date.now() - startedDate.getTime()) / 86400000),
            )
          : 0;

        return (
          <div className="quit-streak-input">
            <div className="quit-streak-top-row">
              <label
                className="quit-streak-label"
                htmlFor={`quit-date-${goal.id}`}
              >
                Quit by date
              </label>
              <input
                id={`quit-date-${goal.id}`}
                type="date"
                value={quitByDate}
                onChange={(e) =>
                  updateProgress(goal.id, {
                    ...data,
                    quitByDate: e.target.value,
                  })
                }
                className="quit-streak-date"
              />
            </div>

            <div className="quit-streak-status-row">
              <span className="quit-streak-status-label">Current streak</span>
              <span className="quit-streak-status-value">
                {streakDays} days
              </span>
            </div>

            {!hasStarted ? (
              <div className="quit-streak-actions">
                <button
                  className="quit-streak-button"
                  onClick={() =>
                    updateProgress(goal.id, {
                      ...data,
                      startedDateKey: getLocalDateKey(new Date()),
                    })
                  }
                >
                  Start
                </button>
              </div>
            ) : (
              <div className="quit-streak-actions">
                <button
                  className="quit-streak-button"
                  onClick={() => {
                    if (
                      !window.confirm(
                        "Reset weed quit streak to today due to relapse?",
                      )
                    ) {
                      return;
                    }
                    updateProgress(goal.id, {
                      ...data,
                      startedDateKey: getLocalDateKey(new Date()),
                    });
                  }}
                >
                  Reset Streak
                </button>
              </div>
            )}

            <div className="target-info">
              {hasStarted
                ? `Started ${formatDateKeyLabel(startedDateKey)}.`
                : "Click Start when you begin your quit streak."}
            </div>
          </div>
        );
      }

      case "streak":
        const currentStreak = data.current || 0;
        const weekProgress = data.weekProgress || 0;
        const maxStreak = goal.targetValue;
        const weeklyTarget = goal.weeklyTarget || 0;

        return (
          <div className="streak-input">
            {/* Current Week Progress */}
            {weeklyTarget > 0 && (
              <div className="week-progress-bar">
                <div className="week-progress-header">
                  <span className="week-progress-label">This Week</span>
                  <span className="week-progress-count">
                    {weekProgress}/{weeklyTarget}
                  </span>
                </div>
                <div className="week-progress-track">
                  {Array.from({ length: weeklyTarget }).map((_, i) => (
                    <button
                      key={i}
                      className={`week-progress-dot ${i < weekProgress ? "completed" : ""}`}
                      onClick={() => {
                        const newProgress = i < weekProgress ? i : i + 1;
                        const newData = { ...data, weekProgress: newProgress };

                        // Auto-increment streak when week completes
                        if (
                          newProgress === weeklyTarget &&
                          weekProgress !== weeklyTarget
                        ) {
                          newData.current = Math.min(
                            maxStreak,
                            currentStreak + 1,
                          );
                          newData.weekProgress = 0; // Reset for next week
                        }

                        updateProgress(goal.id, newData);
                      }}
                      title={`Day ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Streak Counter */}
            <div className="streak-counter">
              <button
                className="streak-button decrement"
                onClick={() =>
                  updateProgress(goal.id, {
                    ...data,
                    current: Math.max(0, currentStreak - 1),
                  })
                }
                disabled={currentStreak === 0}
              >
                −
              </button>

              <div className="streak-display">
                <div className="streak-number">{currentStreak}</div>
                <div className="streak-label">weeks in a row</div>
              </div>

              <button
                className="streak-button increment"
                onClick={() =>
                  updateProgress(goal.id, {
                    ...data,
                    current: Math.min(maxStreak, currentStreak + 1),
                    weekProgress: 0,
                  })
                }
                disabled={currentStreak >= maxStreak}
              >
                +
              </button>
            </div>

            <div className="target-info">
              Target: {maxStreak} {goal.unit} •{" "}
              {currentStreak > 0
                ? `${currentStreak} week${currentStreak !== 1 ? "s" : ""} streak! 🔥`
                : "Start your streak!"}
            </div>
          </div>
        );

      case "milestones":
        const completedMilestonesArray = data.completedMilestones || [];
        const showResetButton =
          goal.id === "launch-app" || goal.id === "writing";

        const handleToggleMilestone = (index) => {
          const completed = [...completedMilestonesArray];
          if (completed.includes(index)) {
            // Remove from completed
            const filtered = completed.filter((i) => i !== index);
            updateProgress(goal.id, { completedMilestones: filtered });
          } else {
            // Add to completed
            completed.push(index);
            updateProgress(goal.id, { completedMilestones: completed });
          }
        };

        return (
          <div className="milestones-input">
            <div className="milestones-grid">
              {goal.milestones.map((milestone, index) => {
                const isCompleted = completedMilestonesArray.includes(index);
                return (
                  <button
                    key={index}
                    className={`milestone-button ${isCompleted ? "completed" : ""}`}
                    onClick={() => handleToggleMilestone(index)}
                  >
                    {isCompleted ? "✓" : index + 1}. {milestone}
                  </button>
                );
              })}
            </div>
            <div className="milestone-footer">
              <div className="target-info">
                {completedMilestonesArray.length} of {goal.milestones.length}{" "}
                milestones completed
              </div>
              {showResetButton && completedMilestonesArray.length > 0 && (
                <button
                  className="reset-milestone-button"
                  onClick={() =>
                    updateProgress(goal.id, { completedMilestones: [] })
                  }
                  title="Reset all milestones"
                >
                  ↺ Reset
                </button>
              )}
            </div>
          </div>
        );

      case "weekly-tracker":
        const weeks = data.weeks || [];
        const currentWeekCount = data.currentWeekCount || 0;

        // Helper to toggle week completion
        const toggleWeek = (weekIndex) => {
          const newWeeks = [...weeks];
          if (!newWeeks[weekIndex]) {
            newWeeks[weekIndex] = { completed: false, count: 0 };
          }
          newWeeks[weekIndex].completed = !newWeeks[weekIndex].completed;
          updateProgress(goal.id, { ...data, weeks: newWeeks });
        };

        // Helper to update current week count
        const updateCurrentWeek = (count) => {
          const parsedCount = parseInt(count);
          const newData = { ...data, currentWeekCount: parsedCount };

          // Auto-mark current week complete if target reached
          if (parsedCount >= goal.targetValue) {
            const currentWeekIndex = weeks.length;
            const newWeeks = [...weeks];
            if (!newWeeks[currentWeekIndex]) {
              newWeeks[currentWeekIndex] = {
                completed: true,
                count: parsedCount,
              };
            }
            newData.weeks = newWeeks;
            newData.currentWeekCount = 0; // Reset for next week
          }

          updateProgress(goal.id, newData);
        };

        // Show last 12 weeks + current
        const displayWeeks = Math.min(weeks.length + 1, 13);
        const startWeek = Math.max(0, weeks.length - 12);

        return (
          <div className="weekly-tracker-input">
            {/* Current Week Counter */}
            <div className="current-week-section">
              <label className="input-label">
                This Week's Progress:
                <input
                  type="number"
                  value={currentWeekCount}
                  onChange={(e) => updateCurrentWeek(e.target.value)}
                  className="number-field"
                  min="0"
                  max={goal.targetValue}
                />
                <span className="unit">
                  / {goal.targetValue} {goal.unit}
                </span>
              </label>
              {currentWeekCount >= goal.targetValue && (
                <span className="week-complete-badge">✅ Target reached!</span>
              )}
            </div>

            {/* Week History Grid */}
            <div className="weeks-grid">
              {Array.from({ length: displayWeeks }).map((_, i) => {
                const weekIndex = startWeek + i;
                const isCurrentWeek = weekIndex === weeks.length;
                const week = weeks[weekIndex];
                const isCompleted =
                  week?.completed ||
                  (isCurrentWeek && currentWeekCount >= goal.targetValue);

                return (
                  <button
                    key={weekIndex}
                    className={`week-checkbox ${isCompleted ? "completed" : ""} ${isCurrentWeek ? "current" : ""}`}
                    onClick={() => !isCurrentWeek && toggleWeek(weekIndex)}
                    disabled={isCurrentWeek}
                    title={`Week ${weekIndex + 1}${isCurrentWeek ? " (Current)" : ""}`}
                  >
                    {isCompleted ? "✓" : weekIndex + 1}
                  </button>
                );
              })}
            </div>

            <div className="target-info">
              {weeks.filter((w) => w.completed).length} weeks completed •
              Current streak: {getCurrentStreak(weeks)}
            </div>
          </div>
        );

      case "tapering":
        const currentPhase = data.currentPhase || 0;
        const startDate = data.startDate ? new Date(data.startDate) : null;
        const dailyCount = data.dailyCount || 0;
        const currentPhaseData = goal.phases[currentPhase];
        const isQuitWeek = currentPhaseData?.dailyLimit === 0;

        // Calculate which week we should be in based on start date
        let autoPhase = 0;
        if (startDate) {
          const weeksSinceStart = Math.floor(
            (new Date() - startDate) / (7 * 24 * 60 * 60 * 1000),
          );
          autoPhase = Math.min(weeksSinceStart, goal.phases.length - 1);
        }

        return (
          <div className="tapering-input">
            {/* Start Date Picker */}
            {!startDate && (
              <div className="date-picker-section">
                <label className="input-label">
                  Set Start Date (Week 1 begins):
                  <input
                    type="date"
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      updateProgress(goal.id, {
                        ...data,
                        startDate: newStartDate,
                        currentPhase: 0,
                      });
                    }}
                    className="date-field"
                  />
                </label>
              </div>
            )}

            {startDate && (
              <>
                {/* Current Phase Display */}
                <div className="phase-info-section">
                  <div className="phase-header">
                    <h4>{currentPhaseData?.description || "Complete!"}</h4>
                    {!isQuitWeek && (
                      <div className="daily-counter">
                        <label className="input-label">
                          Today's count:
                          <input
                            type="number"
                            value={dailyCount}
                            onChange={(e) =>
                              updateProgress(goal.id, {
                                ...data,
                                dailyCount: parseInt(e.target.value),
                              })
                            }
                            className="number-field small"
                            min="0"
                            max={currentPhaseData?.dailyLimit}
                          />
                          <span className="unit">
                            / {currentPhaseData?.dailyLimit} max
                          </span>
                        </label>
                        {dailyCount > currentPhaseData?.dailyLimit && (
                          <span className="over-limit-warning">
                            ⚠️ Over limit!
                          </span>
                        )}
                      </div>
                    )}
                    {isQuitWeek && (
                      <div className="quit-day-celebration">
                        🎯 QUIT DAY! Stay strong! 💪
                      </div>
                    )}
                  </div>
                </div>

                {/* Phase Timeline */}
                <div className="phases-timeline">
                  {goal.phases.map((phase, index) => {
                    const isCurrentPhase = index === currentPhase;
                    const isPastPhase = index < currentPhase;

                    return (
                      <button
                        key={index}
                        className={`phase-button ${
                          isPastPhase
                            ? "completed"
                            : isCurrentPhase
                              ? "current"
                              : "future"
                        }`}
                        onClick={() =>
                          updateProgress(goal.id, {
                            ...data,
                            currentPhase: index,
                            dailyCount: 0,
                          })
                        }
                      >
                        <div className="phase-number">Week {phase.week}</div>
                        <div className="phase-limit">
                          {phase.dailyLimit === 0
                            ? "QUIT"
                            : `${phase.dailyLimit}/day`}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Auto-advance suggestion */}
                {autoPhase > currentPhase && (
                  <div className="auto-advance-suggestion">
                    <p>
                      💡 Based on your start date, you should be on Week{" "}
                      {autoPhase + 1}
                    </p>
                    <button
                      className="advance-button"
                      onClick={() =>
                        updateProgress(goal.id, {
                          ...data,
                          currentPhase: autoPhase,
                          dailyCount: 0,
                        })
                      }
                    >
                      Advance to Week {autoPhase + 1}
                    </button>
                  </div>
                )}

                {/* Reset button */}
                <div className="tapering-footer">
                  <button
                    className="reset-milestone-button"
                    onClick={() =>
                      updateProgress(goal.id, {
                        currentPhase: 0,
                        startDate: null,
                        dailyCount: 0,
                      })
                    }
                  >
                    ↺ Reset Plan
                  </button>
                </div>
              </>
            )}
          </div>
        );

      case "quit-countdown":
        const completedMilestones = data.completedMilestones || [];
        const dailyPuffCount = data.dailyPuffCount || 0;
        const today = new Date();
        const quitDate = new Date(goal.quitDate);
        const daysUntilQuit = Math.ceil(
          (quitDate - today) / (24 * 60 * 60 * 1000),
        );

        // Calculate days since start
        const start = new Date(goal.startDate);
        const daysSinceStart = Math.floor(
          (today - start) / (24 * 60 * 60 * 1000),
        );

        // Find current milestone based on today's date
        const currentMilestone = goal.milestones
          .slice()
          .reverse()
          .find((m) => {
            const mDate = new Date(m.date);
            mDate.setHours(0, 0, 0, 0);
            const todayStart = new Date(today);
            todayStart.setHours(0, 0, 0, 0);
            return mDate <= todayStart;
          });

        // If no current milestone yet, use the first one
        const activeMilestone = currentMilestone || goal.milestones[0];

        // Extract daily limit from milestone description
        const getCurrentDailyLimit = () => {
          if (!activeMilestone) return 15; // default to first limit
          if (activeMilestone.date === goal.quitDate) return 0;
          const match = activeMilestone.description.match(/(\d+) puffs/);
          return match ? parseInt(match[1]) : 15;
        };

        const currentDailyLimit = getCurrentDailyLimit();

        // Toggle milestone completion
        const toggleMilestone = (milestoneDate) => {
          const newCompleted = completedMilestones.includes(milestoneDate)
            ? completedMilestones.filter((d) => d !== milestoneDate)
            : [...completedMilestones, milestoneDate];
          updateProgress(goal.id, {
            ...data,
            completedMilestones: newCompleted,
          });
        };

        return (
          <div className="quit-countdown-input">
            {/* Countdown Display */}
            <div className="countdown-display">
              <div className="countdown-number">
                {daysUntilQuit > 0 ? daysUntilQuit : 0}
              </div>
              <div className="countdown-label">
                {daysUntilQuit > 0
                  ? `day${daysUntilQuit !== 1 ? "s" : ""} until quit day`
                  : daysUntilQuit === 0
                    ? "🎯 TODAY IS QUIT DAY!"
                    : "✅ Quit day passed!"}
              </div>
            </div>

            {/* Daily Puff Counter */}
            {daysUntilQuit >= 0 && (
              <div className="daily-puff-counter">
                <div className="puff-counter-header">
                  <h4>Today's Puff Count</h4>
                  {currentDailyLimit > 0 && (
                    <span className="daily-limit-badge">
                      Limit: {currentDailyLimit} puffs
                    </span>
                  )}
                </div>
                <div className="puff-counter-controls">
                  <button
                    className="puff-button decrement"
                    onClick={() =>
                      updateProgress(goal.id, {
                        ...data,
                        dailyPuffCount: Math.max(0, dailyPuffCount - 1),
                      })
                    }
                    disabled={dailyPuffCount === 0}
                  >
                    −
                  </button>
                  <div className="puff-display">
                    <div className="puff-number">{dailyPuffCount}</div>
                    <div className="puff-label">puffs today</div>
                  </div>
                  <button
                    className="puff-button increment"
                    onClick={() =>
                      updateProgress(goal.id, {
                        ...data,
                        dailyPuffCount: dailyPuffCount + 1,
                      })
                    }
                  >
                    +
                  </button>
                </div>
                {currentDailyLimit > 0 &&
                  dailyPuffCount > currentDailyLimit && (
                    <div className="over-limit-warning">
                      ⚠️ You're over today's limit by{" "}
                      {dailyPuffCount - currentDailyLimit} puff
                      {dailyPuffCount - currentDailyLimit !== 1 ? "s" : ""}
                    </div>
                  )}
                {currentDailyLimit > 0 &&
                  dailyPuffCount <= currentDailyLimit &&
                  dailyPuffCount > 0 && (
                    <div className="within-limit-message">
                      ✓ Within limit ({currentDailyLimit - dailyPuffCount}{" "}
                      remaining)
                    </div>
                  )}
              </div>
            )}

            {/* Milestones Grid */}
            <div className="milestones-countdown-grid">
              {goal.milestones.map((milestone, index) => {
                const milestoneDate = new Date(milestone.date);
                const isCompleted = completedMilestones.includes(
                  milestone.date,
                );
                const isPast = milestoneDate < today;
                const isToday =
                  milestoneDate.toDateString() === today.toDateString();
                const isQuitDay = milestone.date === goal.quitDate;

                return (
                  <button
                    key={index}
                    className={`milestone-countdown-button ${
                      isCompleted
                        ? "completed"
                        : isToday
                          ? "current"
                          : isPast
                            ? "missed"
                            : "future"
                    } ${isQuitDay ? "quit-day" : ""}`}
                    onClick={() => toggleMilestone(milestone.date)}
                  >
                    <div className="milestone-date">
                      {new Date(milestone.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                    <div className="milestone-description">
                      {isCompleted && "✓ "}
                      {milestone.description}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Progress Summary */}
            <div className="target-info">
              {completedMilestones.length} of {goal.milestones.length}{" "}
              milestones completed
              {daysSinceStart >= 0 && (
                <span> • Day {daysSinceStart + 1} of your journey</span>
              )}
            </div>
          </div>
        );

      case "sobriety":
        // Custom logic for quit-drinking goal
        if (goal.id === "quit-drinking") {
          // Always use the config quitDate (reset by updating GOALS_CONFIG)
          const quitDateSob = new Date(`${goal.quitDate}T08:00:00`);
          const now = new Date();

          // Days until quit date (if not yet quit)
          const daysUntilSober =
            now < quitDateSob
              ? Math.ceil((quitDateSob - now) / (24 * 60 * 60 * 1000))
              : 0;

          // Calculate days sober - only if start date has passed
          let daysSober = 0;
          if (now >= quitDateSob) {
            const msSince = now.getTime() - quitDateSob.getTime();
            daysSober = Math.floor(msSince / (24 * 60 * 60 * 1000));
          }

          // Calculate money saved - only if start date has passed
          const weeklyCost = goal.weeklyCost || 0;
          const dailyCost = weeklyCost / 7;
          const moneyOffset = goal.moneyOffset || 0;
          const moneySaved =
            now >= quitDateSob && daysSober > 0
              ? daysSober * dailyCost + moneyOffset
              : 0;
          return (
            <div className="sobriety-tracker">
              {/* Date Picker for Start Date - Hidden for now */}
              {/* <div className="custom-date-picker">
                <label htmlFor="quit-drinking-date">
                  Sobriety Start Date (8:00 AM):
                </label>
                <input
                  type="date"
                  id="quit-drinking-date"
                  value={customStartDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setProgress((prev) => ({
                      ...prev,
                      [goal.id]: {
                        ...prev[goal.id],
                        startDate: newDate,
                      },
                    }));
                  }}
                  className="date-input"
                />
              </div> */}

              {daysUntilSober > 0 ? (
                <div className="countdown-to-quit">
                  <div className="countdown-number">{daysUntilSober}</div>
                  <div className="countdown-label">
                    day{daysUntilSober !== 1 ? "s" : ""} until quit day
                  </div>
                  <div className="quit-date-display">
                    Quit Date:{" "}
                    {new Date(`${goal.quitDate}T08:00:00`).toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric", year: "numeric" },
                    )}
                    , 8:00AM
                  </div>
                </div>
              ) : (
                <>
                  {/* Days Sober Gauge */}
                  <div className="sobriety-gauge">
                    <div className="gauge-header">
                      <span className="gauge-label">Days Sober</span>
                      <span className="gauge-value">{daysSober}</span>
                    </div>
                    <div className="gauge-visual">
                      <svg viewBox="0 0 200 120" className="gauge-svg">
                        {/* Background arc */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#e0e0e0"
                          strokeWidth="20"
                          strokeLinecap="round"
                        />
                        {/* Progress arc - fills based on milestones */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#4caf50"
                          strokeWidth="20"
                          strokeLinecap="round"
                          strokeDasharray={`${Math.min((daysSober / 365) * 251, 251)} 251`}
                          className="gauge-progress"
                        />
                        {/* Center text */}
                        <text
                          x="100"
                          y="90"
                          textAnchor="middle"
                          className="gauge-number"
                          fill="#ffffff"
                          fontSize="32"
                          fontWeight="bold"
                        >
                          {daysSober}
                        </text>
                        <text
                          x="100"
                          y="110"
                          textAnchor="middle"
                          className="gauge-subtitle"
                          fill="#ffffff"
                          fontSize="14"
                        >
                          days
                        </text>
                      </svg>
                    </div>
                    <div className="milestone-badges">
                      {daysSober >= 1 && <span className="badge">✓ 1 Day</span>}
                      {daysSober >= 7 && (
                        <span className="badge">✓ 1 Week</span>
                      )}
                      {daysSober >= 30 && (
                        <span className="badge">✓ 1 Month</span>
                      )}
                      {daysSober >= 90 && (
                        <span className="badge">✓ 3 Months</span>
                      )}
                      {daysSober >= 180 && (
                        <span className="badge">✓ 6 Months</span>
                      )}
                      {daysSober >= 365 && (
                        <span className="badge">✓ 1 Year</span>
                      )}
                    </div>
                  </div>

                  {/* Money Saved Gauge */}
                  <div className="sobriety-gauge">
                    <div className="gauge-header">
                      <span className="gauge-label">Money Saved</span>
                      <span className="gauge-value">
                        ${moneySaved.toFixed(2)}
                      </span>
                    </div>
                    <div className="gauge-visual">
                      <svg viewBox="0 0 200 120" className="gauge-svg">
                        {/* Background arc */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#e0e0e0"
                          strokeWidth="20"
                          strokeLinecap="round"
                        />
                        {/* Progress arc - fills based on money milestones */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#2196f3"
                          strokeWidth="20"
                          strokeLinecap="round"
                          strokeDasharray={`${Math.min((moneySaved / 10000) * 251, 251)} 251`}
                          className="gauge-progress"
                        />
                        {/* Center text */}
                        <text
                          x="100"
                          y="90"
                          textAnchor="middle"
                          className="gauge-number"
                          fill="#ffffff"
                          fontSize="28"
                          fontWeight="bold"
                        >
                          ${Math.floor(moneySaved)}
                        </text>
                        <text
                          x="100"
                          y="110"
                          textAnchor="middle"
                          className="gauge-subtitle"
                          fill="#ffffff"
                          fontSize="14"
                        >
                          saved
                        </text>
                      </svg>
                    </div>
                    <div className="money-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">
                          Daily cost avoided:
                        </span>
                        <span className="breakdown-value">
                          ${dailyCost.toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Weekly savings:</span>
                        <span className="breakdown-value">
                          ${weeklyCost.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Sobriety Date */}
                  <div className="sobriety-footer">
                    <p className="sobriety-date">
                      Sober since:{" "}
                      {new Date(`${goal.quitDate}T08:00:00`).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )}
                      , 8:00AM
                    </p>
                  </div>
                </>
              )}
            </div>
          );
        }

        // Custom logic for quit-vaping goal
        if (goal.id === "quit-vaping") {
          // Use Dec 7, 2025 at 8:00AM as day 0
          const quitDateVape = new Date("2025-12-07T08:00:00");
          const now = new Date();
          // Calculate days smoke-free
          let daysSmokeFree = 0;
          if (now >= quitDateVape) {
            const msSince = now.getTime() - quitDateVape.getTime();
            daysSmokeFree = Math.floor(msSince / (24 * 60 * 60 * 1000));
          }
          // Calculate money saved
          const weeklyCost = goal.weeklyCost || 0;
          const dailyCost = weeklyCost / 7;
          const moneySaved = daysSmokeFree * dailyCost;
          // Days until quit date (if not yet quit)
          const daysUntilQuit =
            now < quitDateVape
              ? Math.ceil((quitDateVape - now) / (24 * 60 * 60 * 1000))
              : 0;
          return (
            <div className="sobriety-tracker">
              {daysUntilQuit > 0 ? (
                <div className="countdown-to-quit">
                  <div className="countdown-number">{daysUntilQuit}</div>
                  <div className="countdown-label">
                    day{daysUntilQuit !== 1 ? "s" : ""} until quit day
                  </div>
                  <div className="quit-date-display">
                    Quit Date: Dec 7, 2025, 8:00AM
                  </div>
                </div>
              ) : (
                <>
                  {/* Days Smoke-Free Gauge */}
                  <div className="sobriety-gauge">
                    <div className="gauge-header">
                      <span className="gauge-label">Days Smoke-Free</span>
                      <span className="gauge-value">{daysSmokeFree}</span>
                    </div>
                    <div className="gauge-visual">
                      <svg viewBox="0 0 200 120" className="gauge-svg">
                        {/* Background arc */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#e0e0e0"
                          strokeWidth="20"
                          strokeLinecap="round"
                        />
                        {/* Progress arc - fills based on milestones */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#4caf50"
                          strokeWidth="20"
                          strokeLinecap="round"
                          strokeDasharray={`${Math.min((daysSmokeFree / 365) * 251, 251)} 251`}
                          className="gauge-progress"
                        />
                        {/* Center text */}
                        <text
                          x="100"
                          y="90"
                          textAnchor="middle"
                          className="gauge-number"
                          fill="#ffffff"
                          fontSize="32"
                          fontWeight="bold"
                        >
                          {daysSmokeFree}
                        </text>
                        <text
                          x="100"
                          y="110"
                          textAnchor="middle"
                          className="gauge-subtitle"
                          fill="#ffffff"
                          fontSize="14"
                        >
                          days
                        </text>
                      </svg>
                    </div>
                    <div className="milestone-badges">
                      {daysSmokeFree >= 1 && (
                        <span className="badge">✓ 1 Day</span>
                      )}
                      {daysSmokeFree >= 7 && (
                        <span className="badge">✓ 1 Week</span>
                      )}
                      {daysSmokeFree >= 30 && (
                        <span className="badge">✓ 1 Month</span>
                      )}
                      {daysSmokeFree >= 90 && (
                        <span className="badge">✓ 3 Months</span>
                      )}
                      {daysSmokeFree >= 180 && (
                        <span className="badge">✓ 6 Months</span>
                      )}
                      {daysSmokeFree >= 365 && (
                        <span className="badge">✓ 1 Year</span>
                      )}
                    </div>
                  </div>

                  {/* Money Saved Gauge */}
                  <div className="sobriety-gauge">
                    <div className="gauge-header">
                      <span className="gauge-label">Money Saved</span>
                      <span className="gauge-value">
                        ${moneySaved.toFixed(2)}
                      </span>
                    </div>
                    <div className="gauge-visual">
                      <svg viewBox="0 0 200 120" className="gauge-svg">
                        {/* Background arc */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#e0e0e0"
                          strokeWidth="20"
                          strokeLinecap="round"
                        />
                        {/* Progress arc - fills based on money milestones */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#2196f3"
                          strokeWidth="20"
                          strokeLinecap="round"
                          strokeDasharray={`${Math.min((moneySaved / 10000) * 251, 251)} 251`}
                          className="gauge-progress"
                        />
                        {/* Center text */}
                        <text
                          x="100"
                          y="90"
                          textAnchor="middle"
                          className="gauge-number"
                          fill="#ffffff"
                          fontSize="28"
                          fontWeight="bold"
                        >
                          ${Math.floor(moneySaved)}
                        </text>
                        <text
                          x="100"
                          y="110"
                          textAnchor="middle"
                          className="gauge-subtitle"
                          fill="#ffffff"
                          fontSize="14"
                        >
                          saved
                        </text>
                      </svg>
                    </div>
                    <div className="money-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">
                          Daily cost avoided:
                        </span>
                        <span className="breakdown-value">
                          ${dailyCost.toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Weekly savings:</span>
                        <span className="breakdown-value">
                          ${weeklyCost.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Sobriety Date */}
                  <div className="sobriety-footer">
                    <p className="sobriety-date">
                      Smoke-free since: Dec 7, 2025, 8:00AM
                    </p>
                  </div>
                </>
              )}
            </div>
          );
        }

        // Custom logic for cannabis-sobriety goal
        if (goal.id === "cannabis-sobriety") {
          // Use Jan 14, 2026 at 10:30AM as the start time
          const quitDateCannabis = new Date("2026-01-14T10:30:00");
          const now = new Date();

          // Calculate time components
          let timeSober = {
            days: 0,
            hours: 0,
            minutes: 0,
          };

          if (now >= quitDateCannabis) {
            const msSince = now.getTime() - quitDateCannabis.getTime();
            timeSober.days = Math.floor(msSince / (24 * 60 * 60 * 1000));
            const remainingMs = msSince % (24 * 60 * 60 * 1000);
            timeSober.hours = Math.floor(remainingMs / (60 * 60 * 1000));
            timeSober.minutes = Math.floor(
              (remainingMs % (60 * 60 * 1000)) / (60 * 1000),
            );
          }

          // Calculate money saved (count upwards at $150 per week)
          const weeklyCost = goal.weeklyCost || 150;
          const dailyCost = weeklyCost / 7;
          const hourlyRate = weeklyCost / (7 * 24);

          let moneySaved = 0;
          if (now >= quitDateCannabis) {
            const hoursSince =
              (now.getTime() - quitDateCannabis.getTime()) / (60 * 60 * 1000);
            moneySaved = hoursSince * hourlyRate;
          }

          // Days until quit date (if not yet quit)
          const daysUntilQuit =
            now < quitDateCannabis
              ? Math.ceil((quitDateCannabis - now) / (24 * 60 * 60 * 1000))
              : 0;

          return (
            <div className="sobriety-tracker">
              {daysUntilQuit > 0 ? (
                <div className="countdown-to-quit">
                  <div className="countdown-number">{daysUntilQuit}</div>
                  <div className="countdown-label">
                    day{daysUntilQuit !== 1 ? "s" : ""} until quit time
                  </div>
                  <div className="quit-date-display">
                    Quit Time: Jan 14, 2026, 10:30AM
                  </div>
                </div>
              ) : (
                <>
                  {/* Time Sober Display */}
                  <div className="sobriety-gauge">
                    <div className="gauge-header">
                      <span className="gauge-label">Time Sober</span>
                      <span className="gauge-value">
                        {timeSober.days > 0 && `${timeSober.days}d `}
                        {timeSober.hours}h {timeSober.minutes}m
                      </span>
                    </div>
                    <div className="gauge-visual">
                      <svg viewBox="0 0 200 120" className="gauge-svg">
                        {/* Background arc */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#e0e0e0"
                          strokeWidth="20"
                          strokeLinecap="round"
                        />
                        {/* Progress arc - fills based on milestones */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#4caf50"
                          strokeWidth="20"
                          strokeLinecap="round"
                          strokeDasharray={`${Math.min((timeSober.days / 365) * 251, 251)} 251`}
                          className="gauge-progress"
                        />
                        {/* Center text */}
                        <text
                          x="100"
                          y="90"
                          textAnchor="middle"
                          className="gauge-number"
                          fill="#ffffff"
                          fontSize="32"
                          fontWeight="bold"
                        >
                          {timeSober.days}
                        </text>
                        <text
                          x="100"
                          y="110"
                          textAnchor="middle"
                          className="gauge-subtitle"
                          fill="#ffffff"
                          fontSize="14"
                        >
                          days
                        </text>
                      </svg>
                    </div>
                    <div className="milestone-badges">
                      {timeSober.days >= 1 && (
                        <span className="badge">✓ 1 Day</span>
                      )}
                      {timeSober.days >= 7 && (
                        <span className="badge">✓ 1 Week</span>
                      )}
                      {timeSober.days >= 30 && (
                        <span className="badge">✓ 1 Month</span>
                      )}
                      {timeSober.days >= 90 && (
                        <span className="badge">✓ 3 Months</span>
                      )}
                      {timeSober.days >= 180 && (
                        <span className="badge">✓ 6 Months</span>
                      )}
                      {timeSober.days >= 365 && (
                        <span className="badge">✓ 1 Year</span>
                      )}
                    </div>
                  </div>

                  {/* Money Saved Gauge */}
                  <div className="sobriety-gauge">
                    <div className="gauge-header">
                      <span className="gauge-label">Money Saved</span>
                      <span className="gauge-value">
                        ${moneySaved.toFixed(2)}
                      </span>
                    </div>
                    <div className="gauge-visual">
                      <svg viewBox="0 0 200 120" className="gauge-svg">
                        {/* Background arc */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#e0e0e0"
                          strokeWidth="20"
                          strokeLinecap="round"
                        />
                        {/* Progress arc - fills based on money milestones */}
                        <path
                          d="M 20 100 A 80 80 0 0 1 180 100"
                          fill="none"
                          stroke="#2196f3"
                          strokeWidth="20"
                          strokeLinecap="round"
                          strokeDasharray={`${Math.min((moneySaved / 10000) * 251, 251)} 251`}
                          className="gauge-progress"
                        />
                        {/* Center text */}
                        <text
                          x="100"
                          y="90"
                          textAnchor="middle"
                          className="gauge-number"
                          fill="#ffffff"
                          fontSize="28"
                          fontWeight="bold"
                        >
                          ${Math.floor(moneySaved)}
                        </text>
                        <text
                          x="100"
                          y="110"
                          textAnchor="middle"
                          className="gauge-subtitle"
                          fill="#ffffff"
                          fontSize="14"
                        >
                          saved
                        </text>
                      </svg>
                    </div>
                    <div className="money-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">Daily savings:</span>
                        <span className="breakdown-value">
                          ${dailyCost.toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Weekly savings:</span>
                        <span className="breakdown-value">
                          ${weeklyCost.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Sobriety Date */}
                  <div className="sobriety-footer">
                    <p className="sobriety-date">
                      Sober since: Jan 14, 2026, 10:30AM
                    </p>
                  </div>
                </>
              )}
            </div>
          );
        }

        // Default sobriety logic for other goals
        const quitDateSob = new Date(goal.quitDate);
        const todaySob = new Date();

        // Calculate days sober (0 if before quit date)
        const daysSober =
          quitDateSob <= todaySob
            ? Math.floor((todaySob - quitDateSob) / (24 * 60 * 60 * 1000))
            : 0;

        // Calculate money saved
        const weeklyCost = goal.weeklyCost || 0;
        const dailyCost = weeklyCost / 7;
        const moneySaved = daysSober * dailyCost;

        // Days until quit date (if not yet quit)
        const daysUntilSober =
          quitDateSob > todaySob
            ? Math.ceil((quitDateSob - todaySob) / (24 * 60 * 60 * 1000))
            : 0;

        return (
          <div className="sobriety-tracker">
            {daysUntilSober > 0 ? (
              <div className="countdown-to-quit">
                <div className="countdown-number">{daysUntilSober}</div>
                <div className="countdown-label">
                  day{daysUntilSober !== 1 ? "s" : ""} until quit day
                </div>
                <div className="quit-date-display">
                  Quit Date:{" "}
                  {quitDateSob.toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
            ) : (
              <>
                {/* Days Sober Gauge */}
                <div className="sobriety-gauge">
                  <div className="gauge-header">
                    <span className="gauge-label">Days Sober</span>
                    <span className="gauge-value">{daysSober}</span>
                  </div>
                  <div className="gauge-visual">
                    <svg viewBox="0 0 200 120" className="gauge-svg">
                      {/* Background arc */}
                      <path
                        d="M 20 100 A 80 80 0 0 1 180 100"
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth="20"
                        strokeLinecap="round"
                      />
                      {/* Progress arc - fills based on milestones */}
                      <path
                        d="M 20 100 A 80 80 0 0 1 180 100"
                        fill="none"
                        stroke="#4caf50"
                        strokeWidth="20"
                        strokeLinecap="round"
                        strokeDasharray={`${Math.min((daysSober / 365) * 251, 251)} 251`}
                        className="gauge-progress"
                      />
                      {/* Center text */}
                      <text
                        x="100"
                        y="90"
                        textAnchor="middle"
                        className="gauge-number"
                        fill="#333"
                        fontSize="32"
                        fontWeight="bold"
                      >
                        {daysSober}
                      </text>
                      <text
                        x="100"
                        y="110"
                        textAnchor="middle"
                        className="gauge-subtitle"
                        fill="#666"
                        fontSize="14"
                      >
                        days
                      </text>
                    </svg>
                  </div>
                  <div className="milestone-badges">
                    {daysSober >= 1 && <span className="badge">✓ 1 Day</span>}
                    {daysSober >= 7 && <span className="badge">✓ 1 Week</span>}
                    {daysSober >= 30 && (
                      <span className="badge">✓ 1 Month</span>
                    )}
                    {daysSober >= 90 && (
                      <span className="badge">✓ 3 Months</span>
                    )}
                    {daysSober >= 180 && (
                      <span className="badge">✓ 6 Months</span>
                    )}
                    {daysSober >= 365 && (
                      <span className="badge">✓ 1 Year</span>
                    )}
                  </div>
                </div>

                {/* Money Saved Gauge */}
                <div className="sobriety-gauge">
                  <div className="gauge-header">
                    <span className="gauge-label">Money Saved</span>
                    <span className="gauge-value">
                      ${moneySaved.toFixed(2)}
                    </span>
                  </div>
                  <div className="gauge-visual">
                    <svg viewBox="0 0 200 120" className="gauge-svg">
                      {/* Background arc */}
                      <path
                        d="M 20 100 A 80 80 0 0 1 180 100"
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth="20"
                        strokeLinecap="round"
                      />
                      {/* Progress arc - fills based on money milestones */}
                      <path
                        d="M 20 100 A 80 80 0 0 1 180 100"
                        fill="none"
                        stroke="#2196f3"
                        strokeWidth="20"
                        strokeLinecap="round"
                        strokeDasharray={`${Math.min((moneySaved / 10000) * 251, 251)} 251`}
                        className="gauge-progress"
                      />
                      {/* Center text */}
                      <text
                        x="100"
                        y="90"
                        textAnchor="middle"
                        className="gauge-number"
                        fill="#333"
                        fontSize="28"
                        fontWeight="bold"
                      >
                        ${Math.floor(moneySaved)}
                      </text>
                      <text
                        x="100"
                        y="110"
                        textAnchor="middle"
                        className="gauge-subtitle"
                        fill="#666"
                        fontSize="14"
                      >
                        saved
                      </text>
                    </svg>
                  </div>
                  <div className="money-breakdown">
                    <div className="breakdown-item">
                      <span className="breakdown-label">
                        Daily cost avoided:
                      </span>
                      <span className="breakdown-value">
                        ${dailyCost.toFixed(2)}
                      </span>
                    </div>
                    <div className="breakdown-item">
                      <span className="breakdown-label">Weekly savings:</span>
                      <span className="breakdown-value">
                        ${weeklyCost.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sobriety Date */}
                <div className="sobriety-footer">
                  <p className="sobriety-date">
                    Sober since:{" "}
                    {quitDateSob.toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </>
            )}
          </div>
        );

      case "percentage":
      default:
        return (
          <div className="slider-input">
            <input
              type="range"
              min="0"
              max="100"
              value={data.current || 0}
              onChange={(e) =>
                updateProgress(goal.id, { current: parseInt(e.target.value) })
              }
              className="goal-slider"
            />
          </div>
        );
    }
  };

  const renderGoalCard = (goal) => {
    const percentage = calculatePercentage(goal);
    const isStreakGoal = goal.type === "streak";
    const progressBarPercentage = isStreakGoal
      ? calculateStreakProgress(goal)
      : percentage;
    const isExpanded = expandedGoals[goal.id];

    return (
      <SortableGoalCard key={goal.id} id={goal.id}>
        {/* Compact tile — always visible */}
        <div
          className="goal-tile-header"
          onClick={() =>
            setExpandedGoals((prev) => ({
              ...prev,
              [goal.id]: !prev[goal.id],
            }))
          }
        >
          <div className="goal-tile-info">
            <h3>{goal.name}</h3>
            <p className="goal-target">{goal.description}</p>
          </div>
          <span className="goal-percentage">{Math.round(percentage)}%</span>
        </div>

        {/* Mini progress bar — always visible */}
        <div className="progress-bar-container mini">
          <div
            className="progress-bar-fill"
            style={{ width: `${progressBarPercentage}%` }}
          />
        </div>

        {/* Expanded detail — shown on click */}
        {isExpanded && (
          <div className="goal-expanded">
            <div className="goal-expanded-actions">
              {goal.actionLink && (
                <a
                  className="goal-link-button"
                  href={goal.actionLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {goal.actionLabel || "Open Link"}
                </a>
              )}
              <button
                className="visibility-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleChecklistVisibility(goal.id);
                }}
                title={
                  checklistVisibility[goal.id] ? "Hide tasks" : "Show tasks"
                }
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  {checklistVisibility[goal.id] ? (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  ) : (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </>
                  )}
                </svg>
              </button>
              <button
                className="reset-button"
                onClick={(e) => {
                  e.stopPropagation();
                  resetProgress(goal.id);
                }}
                title="Reset this goal"
              >
                🔄
              </button>
            </div>
            {renderGoalInput(goal)}
            {checklistVisibility[goal.id] && renderChecklist(goal)}

            {/* Accomplishment log */}
            <div className="accomplishment-log">
              <div className="accomplishment-add-row">
                <input
                  type="date"
                  value={accomplishmentDateInputs[goal.id] || ""}
                  onChange={(e) =>
                    setAccomplishmentDateInputs((prev) => ({
                      ...prev,
                      [goal.id]: e.target.value,
                    }))
                  }
                  className="accomplishment-date-input"
                  onClick={(e) => e.stopPropagation()}
                />
                <input
                  type="text"
                  value={accomplishmentInputs[goal.id] || ""}
                  onChange={(e) =>
                    setAccomplishmentInputs((prev) => ({
                      ...prev,
                      [goal.id]: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addAccomplishment(goal.id);
                  }}
                  placeholder="Log an accomplishment..."
                  className="accomplishment-input"
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="accomplishment-add-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    addAccomplishment(goal.id);
                  }}
                  disabled={!(accomplishmentInputs[goal.id] || "").trim()}
                >
                  + Log
                </button>
              </div>
              {(progress[goal.id]?.accomplishments || []).length > 0 && (
                <ul className="accomplishment-list">
                  {(progress[goal.id]?.accomplishments || [])
                    .slice()
                    .reverse()
                    .map((entry, i, arr) => (
                      <li key={i} className="accomplishment-item">
                        <span className="accomplishment-date">
                          {entry.date}
                        </span>
                        <span className="accomplishment-text">
                          {entry.text}
                        </span>
                        <button
                          className="accomplishment-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteAccomplishment(goal.id, arr.length - 1 - i);
                          }}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            {/* Ideas list */}
            {goal.hasIdeasList && (
              <div className="ideas-list-section">
                <h4 className="ideas-list-title">💡 Ideas</h4>
                <div className="ideas-add-row">
                  <input
                    type="text"
                    value={ideaInputs[goal.id] || ""}
                    onChange={(e) =>
                      setIdeaInputs((prev) => ({
                        ...prev,
                        [goal.id]: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addIdea(goal.id);
                    }}
                    placeholder={goal.ideasPlaceholder || "Add an idea..."}
                    className="idea-input"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    className="idea-add-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      addIdea(goal.id);
                    }}
                    disabled={!(ideaInputs[goal.id] || "").trim()}
                  >
                    + Add
                  </button>
                </div>
                {(progress[goal.id]?.ideas || []).length > 0 && (
                  <ul className="ideas-list">
                    {(progress[goal.id]?.ideas || []).map((idea, i) => (
                      <li key={i} className="idea-item">
                        <span className="idea-text">{idea}</span>
                        <button
                          className="idea-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteIdea(goal.id, i);
                          }}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </SortableGoalCard>
    );
  };

  return (
    <div className={`goals-dashboard center-panel-${centerPanel}`}>
      <div className="goals-container">
        {/* Header */}
        <div className="goals-header">
          <h1>🎯 2026–2027 Goals Progress</h1>
          <p className="subtitle">
            Welcome,{" "}
            {currentUser.charAt(0).toUpperCase() + currentUser.slice(1)}!
          </p>
        </div>

        {/* Message Banner */}
        {message && (
          <div
            className={`message-banner ${message.includes("✅") ? "success" : "error"}`}
          >
            {message}
          </div>
        )}

        {/* Category Navigation */}
        {userGoalsConfig.some((g) => g.category) && (
          <div className="category-nav">
            {Array.from(
              new Set(userGoalsConfig.map((g) => g.category).filter(Boolean)),
            ).map((category) => (
              <button
                key={category}
                className={`category-nav-button ${activeCategory === category ? "active" : ""}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        <div className="dashboard-glance-strip">
          <div className="glance-card">
            <span className="glance-label">Major Outcomes</span>
            <strong className="glance-value">
              {dashboardGlanceStats.outcomesAvg === null
                ? "--"
                : `${dashboardGlanceStats.outcomesAvg}%`}
            </strong>
          </div>
          <div className="glance-card">
            <span className="glance-label">Weekly Goals</span>
            <strong className="glance-value">
              {dashboardGlanceStats.weekDone}/{dashboardGlanceStats.weekTotal}
            </strong>
          </div>
          <div className="glance-card">
            <span className="glance-label">Tasks Done</span>
            <strong className="glance-value">
              {dashboardGlanceStats.taskDone}/{dashboardGlanceStats.taskTotal}
            </strong>
          </div>
          <div className="glance-card">
            <span className="glance-label">Front Burner</span>
            <strong className="glance-value">
              {dashboardGlanceStats.frontAvg === null
                ? "--"
                : `${dashboardGlanceStats.frontAvg}%`}
            </strong>
          </div>
          <div className="glance-card">
            <span className="glance-label">Journal Today</span>
            <strong className="glance-value">
              {dashboardGlanceStats.todayJournalCount}
            </strong>
          </div>
          <div className="glance-card">
            <span className="glance-label">Net Worth</span>
            <strong className="glance-value">
              {currencyFormatter.format(dashboardGlanceStats.netWorth)}
            </strong>
          </div>
        </div>

        <div className="compact-control-row">
          <div className="panel-switcher compact-center-switcher">
            <button
              className={`panel-tab ${centerPanel === "goals" ? "active" : ""}`}
              onClick={() => setCenterPanel("goals")}
            >
              Goals
            </button>
            <button
              className={`panel-tab ${centerPanel === "tasks" ? "active" : ""}`}
              onClick={() => setCenterPanel("tasks")}
            >
              Tasks
            </button>
            <button
              className={`panel-tab ${centerPanel === "hobbies" ? "active" : ""}`}
              onClick={() => setCenterPanel("hobbies")}
            >
              Hobbies
            </button>
          </div>

          <div className="panel-switcher compact-left-switcher">
            <button
              className={`panel-tab ${leftPanel === "weekly" ? "active" : ""}`}
              onClick={() => setLeftPanel("weekly")}
            >
              Weekly
            </button>
            <button
              className={`panel-tab ${leftPanel === "journal" ? "active" : ""}`}
              onClick={() => setLeftPanel("journal")}
            >
              Journal
            </button>
          </div>
        </div>

        {/* ===== Three-column dashboard layout ===== */}
        <div className="dashboard-layout">
          {/* CENTER: Goals + Kanban */}
          <div className="dashboard-main">
            <div className="panel-switcher center-switcher">
              <button
                className={`panel-tab ${centerPanel === "goals" ? "active" : ""}`}
                onClick={() => setCenterPanel("goals")}
              >
                Goals
              </button>
              <button
                className={`panel-tab ${centerPanel === "tasks" ? "active" : ""}`}
                onClick={() => setCenterPanel("tasks")}
              >
                Tasks
              </button>
              <button
                className={`panel-tab ${centerPanel === "hobbies" ? "active" : ""}`}
                onClick={() => setCenterPanel("hobbies")}
              >
                Hobbies
              </button>
            </div>

            {/* Goals — Front & Back Burner */}
            {centerPanel === "goals" && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragEnd={handleDragEnd}
              >
                {currentUser !== "antoin" && (
                  <div className="major-outcomes-section">
                    <div className="major-outcomes-heading-row">
                      <h2 className="burner-heading">⭐ Major Outcomes</h2>
                      <p className="major-outcomes-subtitle">
                        Big results the weekly and routine goals are meant to
                        drive.
                      </p>
                    </div>
                    <div className="major-outcomes-grid">
                      {majorOutcomes.map((outcome) => {
                        const isExpanded = !!editingMajorOutcomes[outcome.id];

                        return (
                          <div
                            key={outcome.id}
                            className={`major-outcome-card ${isExpanded ? "expanded" : "compact"}`}
                          >
                            <div className="major-outcome-header">
                              <span className="major-outcome-icon">
                                {outcome.icon}
                              </span>
                              <div>
                                <h3>{outcome.label}</h3>
                                {isExpanded && <p>{outcome.metricLabel}</p>}
                              </div>
                            </div>

                            <div className="major-outcome-progress-shell">
                              <div
                                className="major-outcome-progress-fill"
                                style={{
                                  width:
                                    outcome.progressPercent === null
                                      ? "18%"
                                      : `${Math.round(outcome.progressPercent)}%`,
                                  opacity:
                                    outcome.progressPercent === null ? 0.35 : 1,
                                }}
                              />
                            </div>

                            <div className="major-outcome-meta">
                              <strong>
                                {outcome.progressPercent === null
                                  ? "Proxy pending"
                                  : `${Math.round(outcome.progressPercent)}%`}
                              </strong>
                              {isExpanded && <span>{outcome.subLabel}</span>}
                            </div>

                            {isExpanded && (
                              <>
                                <p className="major-outcome-support">
                                  <strong>{outcome.supportLabel}:</strong>{" "}
                                  {outcome.supportValue}
                                </p>

                                {outcome.linkedGoals.length > 0 && (
                                  <div className="major-outcome-tags">
                                    {outcome.linkedGoals.map((goal) => (
                                      <span
                                        key={goal.id}
                                        className="major-outcome-tag"
                                      >
                                        {goal.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}

                            <div className="major-outcome-actions">
                              <button
                                className="major-outcome-edit-btn"
                                onClick={() =>
                                  setEditingMajorOutcomes((prev) => ({
                                    ...prev,
                                    [outcome.id]: !prev[outcome.id],
                                  }))
                                }
                              >
                                {isExpanded ? "Done" : "Tune"}
                              </button>
                            </div>

                            {isExpanded && (
                              <div className="major-outcome-editor">
                                <label className="major-outcome-field">
                                  <span>Title</span>
                                  <input
                                    type="text"
                                    value={
                                      majorOutcomeSettings[outcome.id]?.label ||
                                      outcome.label
                                    }
                                    onChange={(e) =>
                                      updateMajorOutcomeSetting(outcome.id, {
                                        label: e.target.value,
                                      })
                                    }
                                  />
                                </label>

                                <label className="major-outcome-field major-outcome-field-small">
                                  <span>Icon</span>
                                  <input
                                    type="text"
                                    maxLength={2}
                                    value={
                                      majorOutcomeSettings[outcome.id]?.icon ||
                                      outcome.icon
                                    }
                                    onChange={(e) =>
                                      updateMajorOutcomeSetting(outcome.id, {
                                        icon: e.target.value,
                                      })
                                    }
                                  />
                                </label>

                                <label className="major-outcome-field">
                                  <span>Logic</span>
                                  <select
                                    value={
                                      majorOutcomeSettings[outcome.id]
                                        ?.targetType || outcome.targetType
                                    }
                                    onChange={(e) =>
                                      updateMajorOutcomeSetting(outcome.id, {
                                        targetType: e.target.value,
                                      })
                                    }
                                  >
                                    <option value="house-equity-percent">
                                      Home equity percent
                                    </option>
                                    <option value="weight-loss">
                                      Weight loss
                                    </option>
                                    <option value="linked-goals-average">
                                      Linked goals average
                                    </option>
                                    <option value="sobriety-days">
                                      Cannabis sobriety days
                                    </option>
                                  </select>
                                </label>

                                <label className="major-outcome-field">
                                  <span>Target Value</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="0.1"
                                    value={
                                      majorOutcomeSettings[outcome.id]
                                        ?.targetValue || outcome.targetValue
                                    }
                                    onChange={(e) =>
                                      updateMajorOutcomeSetting(outcome.id, {
                                        targetValue:
                                          Number(e.target.value) > 0
                                            ? Number(e.target.value)
                                            : 1,
                                      })
                                    }
                                  />
                                </label>

                                <label className="major-outcome-field">
                                  <span>Supporting Goals</span>
                                  <select
                                    multiple
                                    value={
                                      majorOutcomeSettings[outcome.id]
                                        ?.linkedGoalIds || []
                                    }
                                    onChange={(e) =>
                                      updateMajorOutcomeSetting(outcome.id, {
                                        linkedGoalIds: Array.from(
                                          e.target.selectedOptions,
                                        ).map((opt) => opt.value),
                                      })
                                    }
                                  >
                                    {userGoalsConfig.map((goal) => (
                                      <option key={goal.id} value={goal.id}>
                                        {goal.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>

                                <p className="major-outcome-editor-hint">
                                  Changes auto-save.
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="burners-square">
                  {/* 🔥 Front Burner */}
                  <div className="front-burner-section">
                    <h2 className="burner-heading">🔥 Front Burner</h2>
                    <SortableContext
                      items={frontBurner}
                      strategy={verticalListSortingStrategy}
                    >
                      <BurnerDropZone id="front-burner-drop">
                        <div className="goals-list">
                          {frontBurner
                            .map((id) =>
                              userGoalsConfig.find((g) => g.id === id),
                            )
                            .filter(
                              (g) =>
                                g &&
                                (!g.category ||
                                  !userGoalsConfig.some((gc) => gc.category) ||
                                  g.category === activeCategory),
                            )
                            .map((goal) => renderGoalCard(goal))}
                          {frontBurner.length === 0 && (
                            <p className="burner-empty">Drag goals here</p>
                          )}
                        </div>
                      </BurnerDropZone>
                    </SortableContext>
                  </div>

                  {/* 🧊 Back Burner */}
                  <div className="back-burner-section">
                    <h2 className="burner-heading">🧊 Back Burner</h2>
                    <SortableContext
                      items={backBurner}
                      strategy={verticalListSortingStrategy}
                    >
                      <BurnerDropZone id="back-burner-drop">
                        <div className="goals-list">
                          {backBurner
                            .map((id) =>
                              userGoalsConfig.find((g) => g.id === id),
                            )
                            .filter(
                              (g) =>
                                g &&
                                (!g.category ||
                                  !userGoalsConfig.some((gc) => gc.category) ||
                                  g.category === activeCategory),
                            )
                            .map((goal) => renderGoalCard(goal))}
                          {backBurner.length === 0 && (
                            <p className="burner-empty">Drag goals here</p>
                          )}
                        </div>
                      </BurnerDropZone>
                    </SortableContext>
                  </div>
                </div>
              </DndContext>
            )}

            {/* Kanban Tasks Section */}
            {centerPanel === "tasks" && (
              <div className="kanban-section">
                <div className="kanban-header">
                  <h2>📋 Tasks</h2>
                </div>

                <div className="kanban-add-row">
                  <input
                    type="text"
                    value={newTaskText}
                    onChange={(e) => setNewTaskText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addKanbanCard();
                    }}
                    placeholder="Add a new task..."
                    className="kanban-add-input"
                  />
                  <button
                    className="kanban-add-button"
                    onClick={addKanbanCard}
                    disabled={!newTaskText.trim()}
                  >
                    + Add
                  </button>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={kanbanCollisionDetection}
                  onDragStart={handleKanbanDragStart}
                  onDragEnd={handleKanbanDragEnd}
                >
                  <div className="kanban-board">
                    {KANBAN_COLUMNS.map((col) => {
                      const cardIds = kanban[col] || [];
                      const isDone = col === "done";
                      return (
                        <div
                          key={col}
                          className={`kanban-column ${draggedTaskId ? "kanban-column-drop-active" : ""}`}
                        >
                          <div
                            className={`kanban-column-header kanban-col-${col}`}
                          >
                            <span className="kanban-column-title">
                              {KANBAN_COLUMN_LABELS[col]}
                            </span>
                            <span className="kanban-column-count">
                              {cardIds.length}
                            </span>
                          </div>
                          <SortableContext
                            items={cardIds}
                            strategy={verticalListSortingStrategy}
                          >
                            <KanbanColumnDroppable id={col}>
                              {cardIds.length === 0 && (
                                <div className="kanban-empty">
                                  {col === "todo"
                                    ? "No tasks"
                                    : col === "in-progress"
                                      ? "Drag tasks here"
                                      : "Nothing done yet"}
                                </div>
                              )}
                              {cardIds.map((cardId) => {
                                const card = kanban.cards.find(
                                  (c) => c.id === cardId,
                                );
                                if (!card) return null;
                                const checklistLen = (card.checklist || [])
                                  .length;
                                const checklistDone = (
                                  card.checklist || []
                                ).filter((s) => s.done).length;
                                const isCardExpanded =
                                  expandedKanbanCards[card.id];
                                return (
                                  <SortableKanbanCard
                                    key={card.id}
                                    id={card.id}
                                  >
                                    <div className="kanban-card-row">
                                      <span className="kanban-card-text">
                                        {card.text}
                                      </span>
                                      <div className="kanban-card-actions">
                                        {checklistLen > 0 && (
                                          <span className="kanban-subtask-badge">
                                            {checklistDone}/{checklistLen}
                                          </span>
                                        )}
                                        <button
                                          className="kanban-card-expand"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpandCard(card.id);
                                          }}
                                          title="Toggle subtasks"
                                        >
                                          {isCardExpanded ? "▾" : "▸"}
                                        </button>
                                        {!isDone && col === "todo" && (
                                          <button
                                            className="kanban-card-move"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              moveCardToColumn(
                                                card.id,
                                                "in-progress",
                                              );
                                            }}
                                            title="Move to In Progress"
                                          >
                                            ▶
                                          </button>
                                        )}
                                        {col === "in-progress" && (
                                          <button
                                            className="kanban-card-move done"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              moveCardToColumn(card.id, "done");
                                            }}
                                            title="Mark as Done"
                                          >
                                            ✓
                                          </button>
                                        )}
                                        {isDone && (
                                          <button
                                            className="kanban-card-archive"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              archiveKanbanCard(card.id);
                                            }}
                                            title="Archive task"
                                          >
                                            🗄
                                          </button>
                                        )}
                                        {isDone && (
                                          <button
                                            className="kanban-card-delete"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteKanbanCard(card.id);
                                            }}
                                            title="Delete task"
                                          >
                                            ✕
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {isCardExpanded && (
                                      <div
                                        className="kanban-checklist"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {(card.checklist || []).map(
                                          (item, i) => (
                                            <label
                                              key={i}
                                              className={`kanban-checklist-item ${item.done ? "checked" : ""}`}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={item.done}
                                                onChange={() =>
                                                  toggleSubtask(
                                                    setKanban,
                                                    card.id,
                                                    i,
                                                  )
                                                }
                                              />
                                              <span>{item.text}</span>
                                              <button
                                                className="kanban-checklist-delete"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  deleteSubtask(
                                                    setKanban,
                                                    card.id,
                                                    i,
                                                  );
                                                }}
                                              >
                                                ✕
                                              </button>
                                            </label>
                                          ),
                                        )}
                                        <div className="kanban-checklist-add">
                                          <input
                                            type="text"
                                            value={subtaskInputs[card.id] || ""}
                                            onChange={(e) =>
                                              setSubtaskInputs((prev) => ({
                                                ...prev,
                                                [card.id]: e.target.value,
                                              }))
                                            }
                                            onKeyDown={(e) => {
                                              e.stopPropagation();
                                              if (e.key === "Enter")
                                                addSubtask(setKanban, card.id);
                                            }}
                                            placeholder="Add subtask..."
                                            className="kanban-checklist-input"
                                          />
                                          <button
                                            className="kanban-checklist-add-btn"
                                            onClick={() =>
                                              addSubtask(setKanban, card.id)
                                            }
                                            disabled={
                                              !(
                                                subtaskInputs[card.id] || ""
                                              ).trim()
                                            }
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </SortableKanbanCard>
                                );
                              })}
                            </KanbanColumnDroppable>
                          </SortableContext>
                        </div>
                      );
                    })}
                  </div>
                </DndContext>
              </div>
            )}

            {/* Task Archive */}
            {centerPanel === "tasks" && kanbanArchive.length > 0 && (
              <div className="kanban-archive-section">
                <button
                  className="kanban-archive-toggle"
                  onClick={() => setShowKanbanArchive((v) => !v)}
                >
                  <span>{showKanbanArchive ? "▾" : "▸"}</span>
                  <span>🗄 Archived Tasks</span>
                  <span className="kanban-archive-badge">
                    {kanbanArchive.length}
                  </span>
                </button>
                {showKanbanArchive && (
                  <div className="kanban-archive-list">
                    {kanbanArchive.map((card) => (
                      <div key={card.id} className="kanban-archive-card">
                        <div className="kanban-archive-card-text">
                          {card.text}
                        </div>
                        <div className="kanban-archive-card-meta">
                          {card.archivedAt
                            ? new Date(card.archivedAt).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )
                            : ""}
                        </div>
                        <div className="kanban-archive-card-actions">
                          <button
                            className="kanban-archive-restore"
                            onClick={() => unarchiveKanbanCard(card.id)}
                            title="Restore to Done"
                          >
                            ↩ Restore
                          </button>
                          <button
                            className="kanban-archive-delete"
                            onClick={() => deleteFromKanbanArchive(card.id)}
                            title="Permanently delete"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Hobby Projects Kanban Section */}
            {centerPanel === "hobbies" && (
              <div className="kanban-section hobby-kanban-section">
                <div className="kanban-header hobby-kanban-header">
                  <h2>🎨 Hobby Projects</h2>
                </div>

                <div className="kanban-add-row">
                  <div className="hobby-add-fields">
                    <input
                      type="text"
                      value={newHobbyText}
                      onChange={(e) => setNewHobbyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addHobbyCard();
                      }}
                      placeholder="Add a hobby project..."
                      className="kanban-add-input hobby-add-input"
                    />
                    <input
                      type="text"
                      value={newHobbySubtitle}
                      onChange={(e) => setNewHobbySubtitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addHobbyCard();
                      }}
                      placeholder="Finish line (optional)..."
                      className="kanban-add-input hobby-add-subtitle"
                    />
                  </div>
                  <button
                    className="kanban-add-button hobby-add-button"
                    onClick={addHobbyCard}
                    disabled={!newHobbyText.trim()}
                  >
                    + Add
                  </button>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={kanbanCollisionDetection}
                  onDragStart={handleHobbyDragStart}
                  onDragEnd={handleHobbyDragEnd}
                >
                  <div className="kanban-board">
                    {KANBAN_COLUMNS.map((col) => {
                      const cardIds = hobbyKanban[col] || [];
                      const isDone = col === "done";
                      return (
                        <div
                          key={col}
                          className={`kanban-column hobby-kanban-column ${draggedHobbyId ? "kanban-column-drop-active" : ""}`}
                        >
                          <div
                            className={`kanban-column-header hobby-col-${col}`}
                          >
                            <span className="kanban-column-title">
                              {KANBAN_COLUMN_LABELS[col]}
                            </span>
                            <span className="kanban-column-count">
                              {cardIds.length}
                            </span>
                          </div>
                          <SortableContext
                            items={cardIds}
                            strategy={verticalListSortingStrategy}
                          >
                            <KanbanColumnDroppable id={col}>
                              {cardIds.length === 0 && (
                                <div className="kanban-empty">
                                  {col === "todo"
                                    ? "No projects"
                                    : col === "in-progress"
                                      ? "Drag projects here"
                                      : "Nothing finished yet"}
                                </div>
                              )}
                              {cardIds.map((cardId) => {
                                const card = hobbyKanban.cards.find(
                                  (c) => c.id === cardId,
                                );
                                if (!card) return null;
                                const checklistLen = (card.checklist || [])
                                  .length;
                                const checklistDone = (
                                  card.checklist || []
                                ).filter((s) => s.done).length;
                                const isCardExpanded =
                                  expandedKanbanCards[card.id];
                                return (
                                  <SortableKanbanCard
                                    key={card.id}
                                    id={card.id}
                                  >
                                    <div className="kanban-card-row">
                                      <div className="hobby-card-content">
                                        <span className="kanban-card-text">
                                          {card.text}
                                        </span>
                                        {card.subtitle && (
                                          <span className="hobby-card-subtitle">
                                            {card.subtitle}
                                          </span>
                                        )}
                                      </div>
                                      <div className="kanban-card-actions">
                                        {checklistLen > 0 && (
                                          <span className="kanban-subtask-badge hobby">
                                            {checklistDone}/{checklistLen}
                                          </span>
                                        )}
                                        <button
                                          className="kanban-card-expand"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpandCard(card.id);
                                          }}
                                          title="Toggle subtasks"
                                        >
                                          {isCardExpanded ? "▾" : "▸"}
                                        </button>
                                        {!isDone && col === "todo" && (
                                          <button
                                            className="kanban-card-move"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              moveHobbyCardToColumn(
                                                card.id,
                                                "in-progress",
                                              );
                                            }}
                                            title="Move to In Progress"
                                          >
                                            ▶
                                          </button>
                                        )}
                                        {col === "in-progress" && (
                                          <button
                                            className="kanban-card-move done"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              moveHobbyCardToColumn(
                                                card.id,
                                                "done",
                                              );
                                            }}
                                            title="Mark as Done"
                                          >
                                            ✓
                                          </button>
                                        )}
                                        {isDone && (
                                          <button
                                            className="kanban-card-archive"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              archiveHobbyCard(card.id);
                                            }}
                                            title="Archive project"
                                          >
                                            🗄
                                          </button>
                                        )}
                                        {isDone && (
                                          <button
                                            className="kanban-card-delete"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteHobbyCard(card.id);
                                            }}
                                            title="Delete project"
                                          >
                                            ✕
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {isCardExpanded && (
                                      <div
                                        className="kanban-checklist hobby"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {(card.checklist || []).map(
                                          (item, i) => (
                                            <label
                                              key={i}
                                              className={`kanban-checklist-item ${item.done ? "checked" : ""}`}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={item.done}
                                                onChange={() =>
                                                  toggleSubtask(
                                                    setHobbyKanban,
                                                    card.id,
                                                    i,
                                                  )
                                                }
                                              />
                                              <span>{item.text}</span>
                                              <button
                                                className="kanban-checklist-delete"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  deleteSubtask(
                                                    setHobbyKanban,
                                                    card.id,
                                                    i,
                                                  );
                                                }}
                                              >
                                                ✕
                                              </button>
                                            </label>
                                          ),
                                        )}
                                        <div className="kanban-checklist-add">
                                          <input
                                            type="text"
                                            value={subtaskInputs[card.id] || ""}
                                            onChange={(e) =>
                                              setSubtaskInputs((prev) => ({
                                                ...prev,
                                                [card.id]: e.target.value,
                                              }))
                                            }
                                            onKeyDown={(e) => {
                                              e.stopPropagation();
                                              if (e.key === "Enter")
                                                addSubtask(
                                                  setHobbyKanban,
                                                  card.id,
                                                );
                                            }}
                                            placeholder="Add subtask..."
                                            className="kanban-checklist-input"
                                          />
                                          <button
                                            className="kanban-checklist-add-btn"
                                            onClick={() =>
                                              addSubtask(
                                                setHobbyKanban,
                                                card.id,
                                              )
                                            }
                                            disabled={
                                              !(
                                                subtaskInputs[card.id] || ""
                                              ).trim()
                                            }
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </SortableKanbanCard>
                                );
                              })}
                            </KanbanColumnDroppable>
                          </SortableContext>
                        </div>
                      );
                    })}
                  </div>
                </DndContext>
              </div>
            )}

            {/* Hobby Archive */}
            {centerPanel === "hobbies" && hobbyKanbanArchive.length > 0 && (
              <div className="kanban-archive-section">
                <button
                  className="kanban-archive-toggle"
                  onClick={() => setShowHobbyArchive((v) => !v)}
                >
                  <span>{showHobbyArchive ? "▾" : "▸"}</span>
                  <span>🗄 Archived Projects</span>
                  <span className="kanban-archive-badge">
                    {hobbyKanbanArchive.length}
                  </span>
                </button>
                {showHobbyArchive && (
                  <div className="kanban-archive-list">
                    {hobbyKanbanArchive.map((card) => (
                      <div key={card.id} className="kanban-archive-card">
                        <div className="kanban-archive-card-text">
                          {card.text}
                          {card.subtitle && (
                            <span className="kanban-archive-card-subtitle">
                              {" · "}
                              {card.subtitle}
                            </span>
                          )}
                        </div>
                        <div className="kanban-archive-card-meta">
                          {card.archivedAt
                            ? new Date(card.archivedAt).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )
                            : ""}
                        </div>
                        <div className="kanban-archive-card-actions">
                          <button
                            className="kanban-archive-restore"
                            onClick={() => unarchiveHobbyCard(card.id)}
                            title="Restore to Done"
                          >
                            ↩ Restore
                          </button>
                          <button
                            className="kanban-archive-delete"
                            onClick={() => deleteFromHobbyArchive(card.id)}
                            title="Permanently delete"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recurring Section — placeholder for future use */}
            <div className="recurring-section-placeholder">
              {/* Recurring tasks will go here */}
            </div>
          </div>

          {/* LEFT SIDEBAR: Goals for the Week + Daily Journal (above tasks on mobile) */}
          <div className="dashboard-left">
            <div className="panel-switcher side-switcher">
              <button
                className={`panel-tab ${leftPanel === "weekly" ? "active" : ""}`}
                onClick={() => setLeftPanel("weekly")}
              >
                Weekly
              </button>
              <button
                className={`panel-tab ${leftPanel === "journal" ? "active" : ""}`}
                onClick={() => setLeftPanel("journal")}
              >
                Journal
              </button>
            </div>

            {/* 📋 Goals for the Week */}
            {leftPanel === "weekly" && (
              <div className="weekly-goals-section">
                <div className="weekly-goals-header">
                  <h2 className="weekly-goals-heading">
                    📋 Goals for the Week
                  </h2>
                  <button
                    className="weekly-goals-end-btn"
                    onClick={endWeek}
                    title="Archive completed goals and start fresh"
                  >
                    End Week
                  </button>
                </div>

                {/* Input */}
                <div className="weekly-goals-input-row">
                  <input
                    type="text"
                    className="weekly-goals-input"
                    placeholder="Add a goal for this week..."
                    value={newWeeklyGoalText}
                    onChange={(e) => setNewWeeklyGoalText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addWeeklyGoal();
                    }}
                  />
                  <button
                    className="weekly-goals-add-btn"
                    onClick={addWeeklyGoal}
                    disabled={!newWeeklyGoalText.trim()}
                  >
                    +
                  </button>
                </div>

                {/* Active goals */}
                {weeklyGoals.goals.filter((g) => !g.done).length > 0 && (
                  <div className="weekly-goals-list">
                    {weeklyGoals.goals
                      .filter((g) => !g.done)
                      .map((goal) => (
                        <div key={goal.id} className="weekly-goal-item">
                          <input
                            type="checkbox"
                            className="weekly-goal-checkbox"
                            checked={false}
                            onChange={() => toggleWeeklyGoal(goal.id)}
                          />
                          <span className="weekly-goal-text">{goal.text}</span>
                          <button
                            className="weekly-goal-delete"
                            onClick={() => deleteWeeklyGoal(goal.id)}
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                  </div>
                )}

                {/* Completed (crossed-out) goals */}
                {weeklyGoals.goals.filter((g) => g.done).length > 0 && (
                  <div className="weekly-goals-done-section">
                    <div className="weekly-goals-done-label">✓ Done</div>
                    <div className="weekly-goals-list">
                      {weeklyGoals.goals
                        .filter((g) => g.done)
                        .map((goal) => (
                          <div
                            key={goal.id}
                            className="weekly-goal-item weekly-goal-item--done"
                          >
                            <input
                              type="checkbox"
                              className="weekly-goal-checkbox"
                              checked={true}
                              onChange={() => toggleWeeklyGoal(goal.id)}
                            />
                            <span className="weekly-goal-text weekly-goal-text--done">
                              {goal.text}
                            </span>
                            <button
                              className="weekly-goal-delete"
                              onClick={() => deleteWeeklyGoal(goal.id)}
                              title="Remove"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {weeklyGoals.goals.length === 0 && (
                  <p className="weekly-goals-empty">
                    No goals yet — add one above.
                  </p>
                )}

                {/* Archive */}
                {weeklyGoalsArchive.length > 0 && (
                  <div className="weekly-goals-archive">
                    <button
                      className="weekly-goals-archive-toggle"
                      onClick={() => setShowWeeklyArchive((v) => !v)}
                    >
                      {showWeeklyArchive ? "▾" : "▸"} Past Weeks (
                      {weeklyGoalsArchive.length})
                    </button>
                    {showWeeklyArchive && (
                      <div className="weekly-goals-archive-list">
                        {weeklyGoalsArchive.map((week, i) => (
                          <div key={i} className="weekly-goals-archive-week">
                            <div className="weekly-goals-archive-week-label">
                              {week.weekLabel}
                            </div>
                            <ul className="weekly-goals-archive-accomplished">
                              {week.accomplished.map((text, j) => (
                                <li key={j}>{text}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 📓 Daily Journal */}
            {leftPanel === "journal" && (
              <div className="journal-section">
                <div className="journal-header">
                  <h2>📓 Daily Journal</h2>
                  <span className="journal-subtitle">
                    Jot a note in any area — saved with today's date.
                  </span>
                  <button
                    className="journal-collapse-btn"
                    onClick={() => setIsJournalCollapsed((prev) => !prev)}
                  >
                    {isJournalCollapsed ? "Expand" : "Collapse"}
                  </button>
                </div>
                {!isJournalCollapsed &&
                  (() => {
                    const todayKey = getLocalDateKey(new Date());

                    // Group entries by date for the "Past Days" view
                    const entriesByDay = journalEntries.reduce((acc, entry) => {
                      const key = entry.dateKey || "unknown";
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(entry);
                      return acc;
                    }, {});
                    const pastDayKeys = Object.keys(entriesByDay)
                      .filter((k) => k !== todayKey && k !== "unknown")
                      .sort((a, b) => b.localeCompare(a));

                    return (
                      <div className="journal-scroll">
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCorners}
                          onDragEnd={({ active, over }) => {
                            if (!over || active.id === over.id) return;
                            setJournalAreaOrder((prev) =>
                              arrayMove(
                                prev,
                                prev.indexOf(active.id),
                                prev.indexOf(over.id),
                              ),
                            );
                          }}
                        >
                          <SortableContext
                            items={journalAreaOrder}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="journal-grid">
                              {journalAreaOrder.map((areaId) => {
                                const area = JOURNAL_AREAS.find(
                                  (a) => a.id === areaId,
                                );
                                if (!area) return null;
                                const areaAll = journalEntries.filter(
                                  (e) => e.area === area.id,
                                );
                                const todayEntries = areaAll
                                  .filter((e) => e.dateKey === todayKey)
                                  .sort(
                                    (a, b) =>
                                      (b.timestamp || 0) - (a.timestamp || 0),
                                  );
                                const pastCount =
                                  areaAll.length - todayEntries.length;
                                const draft = journalDrafts[area.id] || "";
                                return (
                                  <SortableJournalArea
                                    key={area.id}
                                    id={area.id}
                                  >
                                    <div className="journal-area-header">
                                      <span className="journal-area-emoji">
                                        {area.emoji}
                                      </span>
                                      <span className="journal-area-label">
                                        {area.label}
                                      </span>
                                      <span
                                        className="journal-area-count"
                                        title={`${areaAll.length} total entries`}
                                      >
                                        {areaAll.length}
                                      </span>
                                    </div>
                                    <textarea
                                      className="journal-area-input"
                                      value={draft}
                                      onChange={(e) =>
                                        setJournalDraft(area.id, e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" &&
                                          (e.metaKey || e.ctrlKey)
                                        ) {
                                          e.preventDefault();
                                          addJournalEntry(area.id);
                                        }
                                      }}
                                      placeholder={`What's new in ${area.label.toLowerCase()}?`}
                                      rows={3}
                                    />
                                    <div className="journal-area-actions">
                                      <span className="journal-area-hint">
                                        Ctrl/⌘ + Enter
                                      </span>
                                      <button
                                        className="journal-area-add"
                                        onClick={() => addJournalEntry(area.id)}
                                        disabled={!draft.trim()}
                                      >
                                        + Save Entry
                                      </button>
                                    </div>
                                    {todayEntries.length > 0 && (
                                      <div className="journal-area-entries">
                                        <div className="journal-today-label">
                                          Today
                                        </div>
                                        {todayEntries.map((entry) => {
                                          const isEditing =
                                            editingJournalId === entry.id;
                                          return (
                                            <div
                                              key={entry.id}
                                              className="journal-entry"
                                            >
                                              {isEditing ? (
                                                <div className="journal-entry-edit">
                                                  <textarea
                                                    className="journal-area-input"
                                                    value={editingJournalText}
                                                    onChange={(e) =>
                                                      setEditingJournalText(
                                                        e.target.value,
                                                      )
                                                    }
                                                    onKeyDown={(e) => {
                                                      if (e.key === "Escape") {
                                                        e.preventDefault();
                                                        cancelEditJournalEntry();
                                                      } else if (
                                                        e.key === "Enter" &&
                                                        (e.metaKey || e.ctrlKey)
                                                      ) {
                                                        e.preventDefault();
                                                        saveEditJournalEntry();
                                                      }
                                                    }}
                                                    rows={3}
                                                    autoFocus
                                                  />
                                                  <div className="journal-entry-edit-actions">
                                                    <button
                                                      className="journal-entry-cancel"
                                                      onClick={
                                                        cancelEditJournalEntry
                                                      }
                                                    >
                                                      Cancel
                                                    </button>
                                                    <button
                                                      className="journal-area-add"
                                                      onClick={
                                                        saveEditJournalEntry
                                                      }
                                                      disabled={
                                                        !editingJournalText.trim()
                                                      }
                                                    >
                                                      Save
                                                    </button>
                                                  </div>
                                                </div>
                                              ) : (
                                                <>
                                                  <div className="journal-entry-meta">
                                                    {entry.editedAt && (
                                                      <span className="journal-entry-edited">
                                                        edited
                                                      </span>
                                                    )}
                                                    <button
                                                      className="journal-entry-edit-btn"
                                                      onClick={() =>
                                                        startEditJournalEntry(
                                                          entry,
                                                        )
                                                      }
                                                      title="Edit entry"
                                                    >
                                                      ✎
                                                    </button>
                                                    <button
                                                      className="journal-entry-delete"
                                                      onClick={() =>
                                                        deleteJournalEntry(
                                                          entry.id,
                                                        )
                                                      }
                                                      title="Delete entry"
                                                    >
                                                      ✕
                                                    </button>
                                                  </div>
                                                  <p className="journal-entry-text">
                                                    {entry.text}
                                                  </p>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {pastCount > 0 && (
                                      <div className="journal-past-hint">
                                        {pastCount} past{" "}
                                        {pastCount === 1 ? "entry" : "entries"}{" "}
                                        — see below
                                      </div>
                                    )}
                                  </SortableJournalArea>
                                );
                              })}
                            </div>
                          </SortableContext>
                        </DndContext>

                        {pastDayKeys.length > 0 && (
                          <div className="journal-history">
                            <h3 className="journal-history-heading">
                              📅 Past Days
                            </h3>
                            <div className="journal-history-list">
                              {pastDayKeys.map((dayKey) => {
                                const dayEntries = entriesByDay[dayKey] || [];
                                const isOpen = !!expandedJournalDays[dayKey];
                                const dayLabel = formatDateKeyLabel(dayKey);
                                // Count which areas had entries
                                const areaIds = Array.from(
                                  new Set(dayEntries.map((e) => e.area)),
                                );
                                return (
                                  <div
                                    key={dayKey}
                                    className={`journal-day ${isOpen ? "open" : ""}`}
                                  >
                                    <button
                                      className="journal-day-summary"
                                      onClick={() =>
                                        setExpandedJournalDays((prev) => ({
                                          ...prev,
                                          [dayKey]: !prev[dayKey],
                                        }))
                                      }
                                    >
                                      <span className="journal-day-toggle">
                                        {isOpen ? "▾" : "▸"}
                                      </span>
                                      <span className="journal-day-date">
                                        {dayLabel}
                                      </span>
                                      <span className="journal-day-areas">
                                        {areaIds
                                          .map((aid) => {
                                            const a = JOURNAL_AREAS.find(
                                              (x) => x.id === aid,
                                            );
                                            return a ? a.emoji : null;
                                          })
                                          .filter(Boolean)
                                          .join(" ")}
                                      </span>
                                      <span className="journal-day-count">
                                        {dayEntries.length}{" "}
                                        {dayEntries.length === 1
                                          ? "entry"
                                          : "entries"}
                                      </span>
                                    </button>
                                    {isOpen && (
                                      <div className="journal-day-body">
                                        {JOURNAL_AREAS.map((area) => {
                                          const areaDayEntries = dayEntries
                                            .filter((e) => e.area === area.id)
                                            .sort(
                                              (a, b) =>
                                                (b.timestamp || 0) -
                                                (a.timestamp || 0),
                                            );
                                          if (areaDayEntries.length === 0)
                                            return null;
                                          return (
                                            <div
                                              key={area.id}
                                              className="journal-day-area"
                                            >
                                              <div className="journal-day-area-label">
                                                <span>{area.emoji}</span>
                                                <span>{area.label}</span>
                                              </div>
                                              {areaDayEntries.map((entry) => {
                                                const isEditing =
                                                  editingJournalId === entry.id;
                                                return (
                                                  <div
                                                    key={entry.id}
                                                    className="journal-entry compact"
                                                  >
                                                    {isEditing ? (
                                                      <div className="journal-entry-edit">
                                                        <textarea
                                                          className="journal-area-input"
                                                          value={
                                                            editingJournalText
                                                          }
                                                          onChange={(e) =>
                                                            setEditingJournalText(
                                                              e.target.value,
                                                            )
                                                          }
                                                          onKeyDown={(e) => {
                                                            if (
                                                              e.key === "Escape"
                                                            ) {
                                                              e.preventDefault();
                                                              cancelEditJournalEntry();
                                                            } else if (
                                                              e.key ===
                                                                "Enter" &&
                                                              (e.metaKey ||
                                                                e.ctrlKey)
                                                            ) {
                                                              e.preventDefault();
                                                              saveEditJournalEntry();
                                                            }
                                                          }}
                                                          rows={3}
                                                          autoFocus
                                                        />
                                                        <div className="journal-entry-edit-actions">
                                                          <button
                                                            className="journal-entry-cancel"
                                                            onClick={
                                                              cancelEditJournalEntry
                                                            }
                                                          >
                                                            Cancel
                                                          </button>
                                                          <button
                                                            className="journal-area-add"
                                                            onClick={
                                                              saveEditJournalEntry
                                                            }
                                                            disabled={
                                                              !editingJournalText.trim()
                                                            }
                                                          >
                                                            Save
                                                          </button>
                                                        </div>
                                                      </div>
                                                    ) : (
                                                      <>
                                                        <div className="journal-entry-meta">
                                                          {entry.editedAt && (
                                                            <span className="journal-entry-edited">
                                                              edited
                                                            </span>
                                                          )}
                                                          <button
                                                            className="journal-entry-edit-btn"
                                                            onClick={() =>
                                                              startEditJournalEntry(
                                                                entry,
                                                              )
                                                            }
                                                            title="Edit entry"
                                                          >
                                                            ✎
                                                          </button>
                                                          <button
                                                            className="journal-entry-delete"
                                                            onClick={() =>
                                                              deleteJournalEntry(
                                                                entry.id,
                                                              )
                                                            }
                                                            title="Delete entry"
                                                          >
                                                            ✕
                                                          </button>
                                                        </div>
                                                        <p className="journal-entry-text">
                                                          {entry.text}
                                                        </p>
                                                      </>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
              </div>
            )}
          </div>

          {/* RIGHT: Weekly Rhythm sidebar */}
          <div className="dashboard-sidebar">
            <div className="panel-switcher side-switcher right-switcher">
              <button
                className={`panel-tab ${rightPanel === "money" ? "active" : ""}`}
                onClick={() => setRightPanel("money")}
              >
                Money
              </button>
              <button
                className={`panel-tab ${rightPanel === "sobriety" ? "active" : ""}`}
                onClick={() => setRightPanel("sobriety")}
              >
                Sobriety
              </button>
              <button
                className={`panel-tab ${rightPanel === "weight" ? "active" : ""}`}
                onClick={() => setRightPanel("weight")}
              >
                Weight
              </button>
              <button
                className={`panel-tab ${rightPanel === "thc" ? "active" : ""}`}
                onClick={() => setRightPanel("thc")}
              >
                THC
              </button>
              <button
                className={`panel-tab ${rightPanel === "activity" ? "active" : ""}`}
                onClick={() => setRightPanel("activity")}
              >
                Activity
              </button>
              <button
                className={`panel-tab ${rightPanel === "contract" ? "active" : ""}`}
                onClick={() => setRightPanel("contract")}
              >
                Contract
              </button>
            </div>

            {false && (
              <div className="weekly-rhythm-section">
                <div className="weekly-rhythm-header">
                  <h2>🗓️ Weekly Rhythm</h2>
                </div>
                <div className="weekly-rhythm-grid">
                  {[
                    {
                      day: "Monday",
                      slots: [
                        "Bike ride 🚴",
                        "Tennis (night, starts in June) 🎾",
                      ],
                    },
                    {
                      day: "Tuesday",
                      slots: ["Bike ride 🚴", "Russian lesson (morning) 🇷🇺"],
                    },
                    {
                      day: "Wednesday",
                      slots: ["Bike ride 🚴", "Write ~8pm"],
                    },
                    {
                      day: "Thursday",
                      slots: [
                        "Bike ride 🚴",
                        "Swim (primary day) 🏊‍♂️",
                        "Softball league (potential, June start) 🥎",
                      ],
                    },
                    {
                      day: "Friday",
                      slots: [
                        "Bike ride 🚴",
                        "Swim (backup if missed Thursday) 🏊‍♂️",
                      ],
                    },
                    {
                      day: "Saturday",
                      slots: [
                        "Bike ride 🚴",
                        "Golf (range, course, or Golf Cave) ⛳",
                      ],
                    },
                    {
                      day: "Sunday",
                      slots: [
                        "Bike ride 🚴",
                        "House project planning / Flex day 🏠",
                      ],
                    },
                  ].map(({ day, slots }) => {
                    const isToday =
                      new Date().toLocaleDateString("en-US", {
                        weekday: "long",
                      }) === day;
                    return (
                      <div
                        key={day}
                        className={`rhythm-row ${isToday ? "rhythm-today" : ""}`}
                      >
                        <span className="rhythm-day">{day}</span>
                        <div className="rhythm-slots">
                          {slots.map((s, i) => (
                            <span key={i} className="rhythm-slot">
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {false && (
              <div className="rhythm-note">
                <p>Routine:</p>
                <ol>
                  <li>Fitness</li>
                  <li>Writing</li>
                  <li>Work on Games</li>
                  <li>Play Music</li>
                  <li>Improve Career</li>
                  <li>Save Money</li>
                  <li>YouTube/Russian Lessons</li>
                </ol>
              </div>
            )}

            {rightPanel === "money" && (
              <div className="net-worth-sidebar-section">
                <h2 className="net-worth-sidebar-heading">Net Worth</h2>
                <div className="net-worth-input-grid">
                  <label className="net-worth-field">
                    <span className="net-worth-label">Stock Portfolio</span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      inputMode="decimal"
                      value={portfolioValue}
                      onChange={(e) => setPortfolioValue(e.target.value)}
                      placeholder="0"
                      className="net-worth-input"
                    />
                  </label>
                  <label className="net-worth-field">
                    <span className="net-worth-label">Checking Account</span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      inputMode="decimal"
                      value={checkingValue}
                      onChange={(e) => setCheckingValue(e.target.value)}
                      placeholder="0"
                      className="net-worth-input"
                    />
                  </label>
                  <label className="net-worth-field">
                    <span className="net-worth-label">Current House Value</span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      inputMode="decimal"
                      value={houseValue}
                      onChange={(e) => setHouseValue(e.target.value)}
                      placeholder="0"
                      className="net-worth-input"
                    />
                  </label>
                  <label className="net-worth-field">
                    <span className="net-worth-label">Mortgage Debt</span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      inputMode="decimal"
                      value={mortgageDebtValue}
                      onChange={(e) => setMortgageDebtValue(e.target.value)}
                      placeholder="0"
                      className="net-worth-input"
                    />
                  </label>
                  <label className="net-worth-field">
                    <span className="net-worth-label">RAV4 Loan Balance</span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      inputMode="decimal"
                      value={rav4DebtValue}
                      onChange={(e) => setRav4DebtValue(e.target.value)}
                      placeholder="0"
                      className="net-worth-input"
                    />
                  </label>
                  <label className="net-worth-field">
                    <span className="net-worth-label">
                      Mercedes Loan Balance
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      inputMode="decimal"
                      value={mercedesDebtValue}
                      onChange={(e) => setMercedesDebtValue(e.target.value)}
                      placeholder="0"
                      className="net-worth-input"
                    />
                  </label>
                  <label className="net-worth-field">
                    <span className="net-worth-label">Credit Card Debt</span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      inputMode="decimal"
                      value={creditCardDebtValue}
                      onChange={(e) => setCreditCardDebtValue(e.target.value)}
                      placeholder="0"
                      className="net-worth-input"
                    />
                  </label>
                </div>

                <div className="net-worth-breakdown">
                  <div className="net-worth-line">
                    <span>Assets</span>
                    <strong>
                      {currencyFormatter.format(
                        netWorthSnapshot.portfolio +
                          netWorthSnapshot.checking +
                          netWorthSnapshot.house,
                      )}
                    </strong>
                  </div>
                  <div className="net-worth-line debt">
                    <span>Debt</span>
                    <strong>
                      -{currencyFormatter.format(netWorthSnapshot.totalDebt)}
                    </strong>
                  </div>
                </div>

                <div
                  className={`net-worth-total ${netWorthSnapshot.netWorth >= 0 ? "positive" : "negative"}`}
                >
                  <span>Current Net Worth</span>
                  <strong>
                    {currencyFormatter.format(netWorthSnapshot.netWorth)}
                  </strong>
                </div>
              </div>
            )}

            {/* 🚫 No Drinking Streak Tracker */}
            {rightPanel === "sobriety" &&
              (() => {
                const quitDate = new Date(noDrinkingQuitDate);
                const now = new Date();
                const daysSober = Math.max(
                  0,
                  Math.floor((now - quitDate) / (1000 * 60 * 60 * 24)),
                );
                const dailyCost = 20;
                const moneySaved = daysSober * dailyCost;
                const milestones = [
                  { days: 1, label: "1 Day" },
                  { days: 7, label: "1 Week" },
                  { days: 14, label: "2 Weeks" },
                  { days: 30, label: "1 Month" },
                  { days: 60, label: "2 Months" },
                  { days: 90, label: "3 Months" },
                  { days: 180, label: "6 Months" },
                  { days: 365, label: "1 Year" },
                ];
                const sinceLabel = quitDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                });
                const handleResetStreak = () => {
                  if (
                    !window.confirm(
                      "Reset the No Drinking streak to today? This will clear your current streak.",
                    )
                  ) {
                    return;
                  }
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const y = today.getFullYear();
                  const m = String(today.getMonth() + 1).padStart(2, "0");
                  const d = String(today.getDate()).padStart(2, "0");
                  setNoDrinkingQuitDate(`${y}-${m}-${d}T00:00:00`);
                };
                return (
                  <div className="sobriety-sidebar-section">
                    <div className="sobriety-sidebar-header">
                      <h2 className="sobriety-sidebar-heading">
                        🚫 No Drinking Streak
                      </h2>
                      <button
                        className="sobriety-reset-btn"
                        onClick={handleResetStreak}
                        title="Reset streak to today"
                      >
                        ↻ Reset
                      </button>
                    </div>
                    <div className="sobriety-sidebar-stats">
                      <div className="sobriety-stat-box">
                        <span className="sobriety-stat-value">{daysSober}</span>
                        <span className="sobriety-stat-label">days sober</span>
                      </div>
                      <div className="sobriety-stat-box money">
                        <span className="sobriety-stat-value">
                          ${moneySaved.toLocaleString()}
                        </span>
                        <span className="sobriety-stat-label">saved</span>
                      </div>
                    </div>
                    <div className="sobriety-sidebar-milestones">
                      {milestones.map((m) => (
                        <span
                          key={m.days}
                          className={`sobriety-badge ${daysSober >= m.days ? "earned" : ""}`}
                        >
                          {daysSober >= m.days ? "✓" : "○"} {m.label}
                        </span>
                      ))}
                    </div>
                    <div className="sobriety-sidebar-footer">
                      Since {sinceLabel} · ${dailyCost}/day
                    </div>
                  </div>
                );
              })()}

            {/* ⚖️ Weight Tracker */}
            {rightPanel === "weight" && (
              <div className="weight-tracker-section">
                <h2 className="weight-tracker-heading">⚖️ Weight Tracker</h2>
                <div className="weight-input-row">
                  <input
                    type="number"
                    step="0.1"
                    value={weightInput}
                    onChange={(e) => setWeightInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addWeightEntry();
                    }}
                    placeholder="Enter weight (lbs)..."
                    className="weight-input"
                  />
                  <button
                    className="weight-add-btn"
                    onClick={addWeightEntry}
                    disabled={!weightInput}
                  >
                    + Log
                  </button>
                </div>
                {weightLog.length > 0 && (
                  <>
                    <canvas
                      ref={weightCanvasRef}
                      className="weight-graph-canvas"
                    />
                    <div className="weight-current">
                      Current:{" "}
                      <strong>
                        {weightLog[weightLog.length - 1].weight} lbs
                      </strong>
                      <span className="weight-date-label">
                        {" "}
                        ({weightLog[weightLog.length - 1].date})
                      </span>
                    </div>
                    <div className="weight-log-entries">
                      {weightLog
                        .slice()
                        .reverse()
                        .map((entry, i, arr) => (
                          <div key={i} className="weight-log-entry">
                            <span className="weight-log-date">
                              {entry.date}
                            </span>
                            <span className="weight-log-value">
                              {entry.weight} lbs
                            </span>
                            <button
                              className="weight-log-delete"
                              onClick={() =>
                                deleteWeightEntry(arr.length - 1 - i)
                              }
                              title="Remove"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* 🌿 THC Tracker */}
            {rightPanel === "thc" && (
              <div className="thc-tracker-section">
                <h2 className="thc-tracker-heading">🌿 THC Tracker</h2>
                <div className="thc-dose-buttons">
                  {[2.5, 5, 10].map((dose) => (
                    <button
                      key={dose}
                      className="thc-dose-btn"
                      onClick={() => addThcDose(dose)}
                    >
                      +{dose}mg
                    </button>
                  ))}
                </div>
                <div className="thc-today-total">
                  Today: <strong>{todayThcTotalMg.toFixed(1)} mg</strong>
                  <span className="thc-goal-note">
                    {" "}
                    Goal: trend down over time
                  </span>
                </div>

                {thcDailyTotals.length > 0 && (
                  <>
                    <canvas ref={thcCanvasRef} className="thc-graph-canvas" />
                    <div className="thc-log-entries">
                      {thcDailyTotals
                        .slice()
                        .reverse()
                        .map((day) => (
                          <div key={day.dateKey} className="thc-log-entry">
                            <span className="thc-log-date">
                              {day.dateLabel}
                            </span>
                            <span className="thc-log-value">
                              {day.totalMg.toFixed(1)} mg
                            </span>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* 📰 Accomplishment Feed */}
            {rightPanel === "activity" && (
              <div className="accomplishment-feed-section">
                <h2 className="accomplishment-feed-heading">
                  📰 Recent Activity
                </h2>
                <div className="accomplishment-feed-list">
                  {(() => {
                    // Collect all accomplishments across all goals with goal name
                    const allEntries = [];
                    userGoalsConfig.forEach((goal) => {
                      const accs = progress[goal.id]?.accomplishments || [];
                      accs.forEach((entry, idx) => {
                        allEntries.push({
                          goalName: goal.name,
                          goalId: goal.id,
                          text: entry.text,
                          date: entry.date,
                          timestamp: entry.timestamp || idx,
                        });
                      });
                    });
                    // Sort newest first, show last 20
                    allEntries.sort(
                      (a, b) => (b.timestamp || 0) - (a.timestamp || 0),
                    );
                    const shown = allEntries.slice(0, 20);

                    if (shown.length === 0) {
                      return (
                        <p className="feed-empty">
                          No accomplishments logged yet.
                        </p>
                      );
                    }

                    return shown.map((entry, i) => (
                      <div key={i} className="feed-entry">
                        <span className="feed-date">{entry.date}</span>
                        <span className="feed-goal-name">{entry.goalName}</span>
                        <span className="feed-text">{entry.text}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {rightPanel === "contract" && (
              <div className="contract-section">
                <div className="contract-header-row">
                  <h2 className="contract-heading">🤝 Quarterly Contract</h2>
                  <span
                    className={`contract-status-pill ${contractSummary.owesNow ? "danger" : contractSummary.quarterPassed ? "success" : "pending"}`}
                  >
                    {contractSummary.owesNow
                      ? `${currencyFormatter.format(contractConfig.amountOwed || 500)} owed`
                      : contractSummary.quarterPassed
                        ? "On track"
                        : "In progress"}
                  </span>
                </div>

                <div className="contract-config-grid">
                  <label className="contract-field">
                    <span>Penalty Amount ($)</span>
                    <input
                      type="number"
                      min="0"
                      step="50"
                      value={contractConfig.amountOwed}
                      onChange={(e) =>
                        updateContractConfig(
                          "amountOwed",
                          Math.max(0, Number(e.target.value) || 0),
                        )
                      }
                    />
                  </label>
                  <label className="contract-field">
                    <span>Quarter Start</span>
                    <input
                      type="date"
                      min={CONTRACT_PROGRAM_START_KEY}
                      value={contractConfig.quarterStart || ""}
                      onChange={(e) =>
                        updateContractConfig("quarterStart", e.target.value)
                      }
                    />
                  </label>
                  <label className="contract-field">
                    <span>Required Passing Weeks (no misses)</span>
                    <input
                      type="number"
                      min="1"
                      max={contractConfig.totalWeeks || 13}
                      value={contractSummary.totalWeeks}
                      disabled
                      readOnly
                    />
                  </label>
                  <label className="contract-field">
                    <span>Weeks In Quarter</span>
                    <input
                      type="number"
                      min="1"
                      max="16"
                      value={contractConfig.totalWeeks}
                      onChange={(e) =>
                        updateContractConfig(
                          "totalWeeks",
                          Math.max(1, Number(e.target.value) || 13),
                        )
                      }
                    />
                  </label>
                  <label className="contract-field">
                    <span>Quit THC By (from Front Burner)</span>
                    <input
                      type="text"
                      value={formatDateKeyLabel(contractSummary.quitByDateKey)}
                      readOnly
                      disabled
                    />
                  </label>
                  <label className="contract-field">
                    <span>Bike / week target</span>
                    <input
                      type="number"
                      min="0"
                      value={contractConfig.bikeTarget}
                      onChange={(e) =>
                        updateContractConfig(
                          "bikeTarget",
                          Math.max(0, Number(e.target.value) || 0),
                        )
                      }
                    />
                  </label>
                  <label className="contract-field">
                    <span>Swim / week target</span>
                    <input
                      type="number"
                      min="0"
                      value={contractConfig.swimTarget}
                      onChange={(e) =>
                        updateContractConfig(
                          "swimTarget",
                          Math.max(0, Number(e.target.value) || 0),
                        )
                      }
                    />
                  </label>
                  <label className="contract-field">
                    <span>Writing sessions / week</span>
                    <input
                      type="number"
                      min="0"
                      value={contractConfig.writingSessionsTarget}
                      onChange={(e) =>
                        updateContractConfig(
                          "writingSessionsTarget",
                          Math.max(0, Number(e.target.value) || 0),
                        )
                      }
                    />
                  </label>
                  <label className="contract-field">
                    <span>Writing words / week</span>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={contractConfig.writingWordsTarget}
                      onChange={(e) =>
                        updateContractConfig(
                          "writingWordsTarget",
                          Math.max(0, Number(e.target.value) || 0),
                        )
                      }
                    />
                  </label>
                </div>

                <div className="contract-metrics">
                  <div className="contract-metric-card">
                    <span>Passing Weeks</span>
                    <strong>
                      {contractSummary.passingWeeksSoFar}/
                      {contractSummary.requiredPassingWeeks}
                    </strong>
                  </div>
                  <div className="contract-metric-card">
                    <span>Weeks Remaining</span>
                    <strong>{contractSummary.remainingWeeks}</strong>
                  </div>
                  <div className="contract-metric-card">
                    <span>Weed Clause</span>
                    <strong>{contractSummary.weedClauseLabel}</strong>
                  </div>
                </div>

                <div className="contract-week-inputs">
                  <h3>Auto-Synced From Front Burner</h3>
                  <div className="contract-current-week-grid">
                    <label className="contract-field">
                      <span>Bike sessions</span>
                      <input
                        type="number"
                        value={contractSummary.currentWeekBikeRides || 0}
                        readOnly
                        disabled
                      />
                    </label>
                    <label className="contract-field">
                      <span>Swim sessions</span>
                      <input
                        type="number"
                        value={contractSummary.currentWeekSwims || 0}
                        readOnly
                        disabled
                      />
                    </label>
                    <label className="contract-field">
                      <span>Writing sessions</span>
                      <input
                        type="number"
                        value={contractSummary.currentWeekWritingSessions || 0}
                        readOnly
                        disabled
                      />
                    </label>
                    <label className="contract-field">
                      <span>Writing words</span>
                      <input
                        type="number"
                        value={contractSummary.currentWeekWritingWords || 0}
                        readOnly
                        disabled
                      />
                    </label>
                    <label className="contract-field">
                      <span>Quit streak started</span>
                      <input
                        type="text"
                        value={contractSummary.hasQuitStarted ? "Yes" : "No"}
                        readOnly
                        disabled
                      />
                    </label>
                    <label className="contract-field">
                      <span>Quit streak days</span>
                      <input
                        type="number"
                        value={contractSummary.quitStreakDays || 0}
                        readOnly
                        disabled
                      />
                    </label>
                  </div>
                  <div className="contract-actions-row">
                    <button
                      className="contract-action-btn secondary"
                      onClick={finalizeQuarterContract}
                    >
                      Finalize Quarter
                    </button>
                  </div>
                </div>

                <div className="contract-week-table-wrap">
                  <table className="contract-week-table">
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th>Move</th>
                        <th>Write</th>
                        <th>Weed</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contractSummary.weekRows.map((row) => (
                        <tr
                          key={row.weekStart}
                          className={
                            row.isCurrentWeek ? "contract-current-row" : ""
                          }
                        >
                          <td>{row.label}</td>
                          <td>
                            {row.bikeRides}/{contractConfig.bikeTarget} bike,{" "}
                            {row.swims}/{contractConfig.swimTarget} swim
                          </td>
                          <td>
                            {row.writingSessions}/
                            {contractConfig.writingSessionsTarget} sessions or{" "}
                            {row.writingWords.toLocaleString()}/
                            {Number(
                              contractConfig.writingWordsTarget || 0,
                            ).toLocaleString()}{" "}
                            words
                          </td>
                          <td>
                            {row.relapse
                              ? "Relapse"
                              : row.inTaperWindow
                                ? "Taper window"
                                : "Clean"}
                          </td>
                          <td>
                            <span
                              className={`contract-week-badge ${
                                row.status === "pass"
                                  ? "pass"
                                  : row.status === "miss"
                                    ? "fail"
                                    : "pending"
                              }`}
                            >
                              {row.status === "pass"
                                ? "Pass"
                                : row.status === "miss"
                                  ? "Miss"
                                  : row.status === "in-progress"
                                    ? "In Progress"
                                    : "Pending"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!contractSummary.canStillPass && !contractSummary.owesNow && (
                  <p className="contract-warning-text">
                    You cannot mathematically hit the required passing weeks
                    with the remaining weeks.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="save-section">
          <span className="auto-save-status">
            {saving ? "💾 Saving..." : message || "💾 Auto-save enabled"}
          </span>
          <button
            onClick={handleSave}
            disabled={saving}
            className="save-button"
          >
            {saving ? "Saving..." : "Save Now"}
          </button>
        </div>

        {/* Footer */}
        <div className="goals-footer">
          Last updated: {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
